import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ReactSelect from 'react-select';
import api from '../../api';
import { AuthContext } from '../../components/AuthContext';
import DetailSection from '../../components/common/DetailSection';
import DetailField from '../../components/common/DetailField';
import StatusBadge from '../../components/common/StatusBadge';
import CopyButton from '../../components/common/CopyButton';
import { InlineLoader, EmptyState } from '../../components/common/StateViews';
import { formatINR } from '../../components/common/Money';
import Modal from '../../components/Modal';
import Pagination from '../../components/Pagination';
import useDarkMode from '../../hooks/useDarkMode';
import Swal from 'sweetalert2';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  CheckIcon,
  XMarkIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  DocumentDuplicateIcon,
  CurrencyRupeeIcon,
  SparklesIcon,
  BellIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ProformaInvoice = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Form state
  const [form, setForm] = useState({
    quotationType: '',
    quotation: null,
    daysRequiredForDelivery: 1,
    items: [],
    paymentStatus: 'TOTAL_CREDIT',
    advanceAmount: 0,
    balanceAmount: 0,
    totalAmount: 0,
    subTotal: 0,
    discountAmount: 0,
    taxAmount: 0,
    cartage: 0,
    packingAndForwarding: 0,
    labour: 0,
    roundOff: 0,
    remarks: ''
  });

  // UI states
  const [modalVisible, setModalVisible] = useState(false);
  const [edit, setEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [quotationSelectionModal, setQuotationSelectionModal] = useState({ open: false, step: 1 });
  const [viewModal, setViewModal] = useState({ open: false, proformaInvoice: null });
  const [viewLinks, setViewLinks] = useState({ indents: [], goodsReceipts: [] });
  const [deliveryOrderModal, setDeliveryOrderModal] = useState({ 
    open: false, 
    proformaInvoiceId: null, 
    assignedOrders: [] 
  });
  const [completionModal, setCompletionModal] = useState({
    open: false,
    proformaInvoiceId: null,
    deliveryOrders: [],
    selectedLastDeliveryOrderIds: []
  });
  const [reservedDetailsModal, setReservedDetailsModal] = useState({ open: false, item: null, details: [] });
  const [stockStatusModal, setStockStatusModal] = useState({ 
    open: false, 
    proformaInvoice: null, 
    selectedItems: [] 
  });
  const [quotationSearch, setQuotationSearch] = useState('');
  
  // Data states
  const [quotations, setQuotations] = useState([]);
  const [proformaInvoices, setProformaInvoices] = useState([]);
  const [availableDeliveryOrders, setAvailableDeliveryOrders] = useState([]);
  const [selectedDeliveryOrders, setSelectedDeliveryOrders] = useState([]);
  const [salesmenList, setSalesmenList] = useState([]);
  
  // Animation and Alert states
  const [isAnimating, setIsAnimating] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [itemAnimations, setItemAnimations] = useState({});
  const [buttonLoading, setButtonLoading] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    paymentStatus: '',
    deliveryStatus: '',
    salesman: '',
    search: '',
    itemStockStatus: '',
    overdue: false
  });
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [attention, setAttention] = useState({ 'to-be-ordered': 0, 'goods-received': 0, 'to-be-billed': 0, overdue: 0 });

  // Stock status options. "goods-received" is set automatically by the goods
  // received flow and is shown for reference only (not manually selectable).
  const stockStatusOptions = [
    { value: 'to-be-reserved', label: 'To be Reserved' },
    { value: 'to-be-ordered', label: 'To Be Ordered' },
    { value: 'to-be-billed', label: 'To be Billed' },
    { value: 'goods-received', label: 'Goods Received (auto)', isDisabled: true }
  ];

  // Payment status options
  const paymentStatusOptions = [
    { value: 'TOTAL_CREDIT', label: 'Total Credit' },
    { value: 'ADVANCE_PARTIAL', label: 'Advance (Partial Payment)' },
    { value: 'FULL_PAID', label: 'Full Paid' }
  ];

  const getMatchingPIItem = (orderItem, piItems) => {
    if (!piItems || piItems.length === 0) return null;
    return piItems.find(piItem => {
      // 1. Match by Item ID
      const orderItemIdStr = orderItem.itemId?._id?.toString() || orderItem.itemId?.toString();
      const piItemIdStr = piItem.item?._id?.toString() || piItem.item?.toString();
      if (orderItemIdStr && piItemIdStr && orderItemIdStr === piItemIdStr) {
        return true;
      }
      // 2. Match by Name as fallback
      const orderItemName = (orderItem.Name || orderItem.itemName || '').toLowerCase().trim();
      const piItemName = (piItem.itemName || piItem.Name || piItem.item?.Name || '').toLowerCase().trim();
      if (orderItemName && piItemName && orderItemName === piItemName) {
        return true;
      }
      return false;
    });
  };

  const reservedSummary = useMemo(() => {
    const details = reservedDetailsModal.details || [];
    return details.reduce((acc, detail) => {
      acc.totalReservedQuantity += Number(detail.reservedQuantity || 0);
      if (detail.pricingMode === 'sqft') {
        const unit = detail.coverageAreaUnit === 'sqm' ? 'sqm' : 'sqft';
        if (unit === 'sqm') {
          acc.totalReservedAreaSqm += Number(detail.reservedCoverageArea || 0);
        } else {
          acc.totalReservedAreaSqft += Number(detail.reservedCoverageArea || 0);
        }
      }
      return acc;
    }, {
      totalReservedQuantity: 0,
      totalReservedAreaSqft: 0,
      totalReservedAreaSqm: 0
    });
  }, [reservedDetailsModal.details]);

  // Refs for keyboard navigation
  const formRef = useRef(null);
  const searchRef = useRef(null);
  const newButtonRef = useRef(null);
  const quotationTypeRef = useRef(null);
  const paymentStatusRef = useRef(null);
  const advanceAmountRef = useRef(null);

  // Keyboard shortcuts state
  const [keyboardHelp, setKeyboardHelp] = useState(false);

  // Dark mode (synced from DashboardLayout via storage/darkModeChange events)
  const isDarkMode = useDarkMode();

  // Quotation filter
  const [quotationTypeFilter, setQuotationTypeFilter] = useState('all');
  const [quotationSortBy, setQuotationSortBy] = useState('date-desc');

  // Dark mode CSS injection for form elements
  useEffect(() => {
    const styleId = 'proforma-invoice-dark-css';
    let el = document.getElementById(styleId);
    if (isDarkMode) {
      if (!el) { el = document.createElement('style'); el.id = styleId; document.head.appendChild(el); }
      el.textContent = `
        .pi-dark input:not([type=checkbox]):not([type=radio]),
        .pi-dark textarea,
        .pi-dark select {
          background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important;
        }
        .pi-dark input::placeholder, .pi-dark textarea::placeholder { color: #9ca3af !important; }
        .pi-dark input:focus, .pi-dark textarea:focus, .pi-dark select:focus {
          border-color: #60a5fa !important; box-shadow: 0 0 0 2px rgba(96,165,250,0.2) !important;
        }
        .pi-dark .react-select__control { background-color: #374151 !important; border-color: #4b5563 !important; }
        .pi-dark .react-select__single-value { color: #f3f4f6 !important; }
        .pi-dark .react-select__menu { background-color: #1f2937 !important; }
        .pi-dark .react-select__option { color: #f3f4f6 !important; }
        .pi-dark .react-select__option--is-focused { background-color: #374151 !important; }
        .pi-dark .react-select__option--is-selected { background-color: #2563eb !important; }
        .pi-dark .react-select__indicator { color: #9ca3af !important; }
        .pi-dark .react-select__placeholder { color: #9ca3af !important; }
        .pi-dark .swal2-popup { background-color: #1f2937 !important; color: #f3f4f6 !important; }
        .pi-dark .swal2-title { color: #f3f4f6 !important; }
        .pi-dark .swal2-html-container { color: #d1d5db !important; }
        .pi-dark .react-datepicker-wrapper input { background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important; }
      `;
    } else if (el) {
      el.remove();
    }
    return () => { const e = document.getElementById(styleId); if (e) e.remove(); };
  }, [isDarkMode]);

  // Animation and notification helper functions
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: 'success' }), 3000);
  };

  const animateItem = (itemId, animationType = 'pulse') => {
    setItemAnimations(prev => ({ ...prev, [itemId]: animationType }));
    setTimeout(() => {
      setItemAnimations(prev => ({ ...prev, [itemId]: '' }));
    }, 1000);
  };

  const setButtonLoadingState = (buttonId, loading = true) => {
    setButtonLoading(prev => ({ ...prev, [buttonId]: loading }));
  };

  const showSuccessAlert = (title, message) => {
    Swal.fire({
      icon: 'success',
      title,
      text: message,
      confirmButtonColor: '#10B981',
      showConfirmButton: false,
      timer: 2000,
      timerProgressBar: true,
      background: '#F0FDF4',
      color: '#065F46'
    });
  };

  const showErrorAlert = (title, message) => {
    Swal.fire({
      icon: 'error',
      title,
      text: message,
      confirmButtonColor: '#EF4444',
      background: '#FEF2F2',
      color: '#991B1B'
    });
  };

  const showWarningAlert = (title, message) => {
    return Swal.fire({
      icon: 'warning',
      title,
      text: message,
      showCancelButton: true,
      confirmButtonColor: '#F59E0B',
      cancelButtonColor: '#6B7280',
      confirmButtonText: 'Yes, proceed!',
      cancelButtonText: 'Cancel',
      background: '#FFFBEB',
      color: '#92400E'
    });
  };

  const showInfoAlert = (title, message) => {
    Swal.fire({
      icon: 'info',
      title,
      text: message,
      confirmButtonColor: '#3B82F6',
      background: '#EFF6FF',
      color: '#1E3A8A'
    });
  };

  useEffect(() => {
    fetchProformaInvoices();
    fetchAttentionSummary();
    
    // Welcome animation
    setTimeout(() => {
      showNotification('Welcome to Proforma Invoice Management! 🎉', 'success');
    }, 1000);
  }, [page, limit, filters]);

  const fetchAttentionSummary = async () => {
    try {
      const response = await api.get('/proforma-invoices/attention-summary', {
        params: { companyId: user.user.company?._id || user.user.company }
      });
      if (response.data?.success) {
        setAttention(response.data.counts || {});
      }
    } catch (error) {
      console.error('Error fetching attention summary:', error);
    }
  };

  // Fetch salesmen list for filter
  useEffect(() => {
    const fetchSalesmenList = async () => {
      try {
        const response = await api.get('/proforma-invoices/salesmen');
        setSalesmenList(response.data || []);
      } catch (err) {
        console.error('Error fetching salesmen list:', err);
      }
    };
    fetchSalesmenList();
  }, []);

  // Auto-update advance amount when total amount changes and payment status requires it
  useEffect(() => {
    if (form.paymentStatus === 'FULL_PAID') {
      // For full paid, advance amount should always equal total amount
      if (form.advanceAmount !== form.totalAmount) {
        setForm(prev => ({
          ...prev,
          advanceAmount: prev.totalAmount,
          balanceAmount: 0
        }));
      }
    } else if (form.paymentStatus === 'TOTAL_CREDIT') {
      // For total credit, advance amount should always be 0
      if (form.advanceAmount !== 0) {
        setForm(prev => ({
          ...prev,
          advanceAmount: 0,
          balanceAmount: prev.totalAmount
        }));
      }
    } else {
      // For partial payment, just update balance amount
      const balanceAmount = Math.max(0, form.totalAmount - form.advanceAmount);
      if (form.balanceAmount !== balanceAmount) {
        setForm(prev => ({
          ...prev,
          balanceAmount
        }));
      }
    }
  }, [form.totalAmount, form.paymentStatus, form.advanceAmount, form.balanceAmount]);

  const fetchProformaInvoices = async () => {
    try {
      setLoading(true);
      setIsAnimating(true);
      
      const response = await api.get('/proforma-invoices', {
        params: {
          page,
          limit,
          companyId: user.user.company?._id || user.user.company,
          ...filters
        }
      });
      
      setProformaInvoices(response.data.proformaInvoices);
      setTotalPages(response.data.totalPages);
      setTotal(response.data.total || 0);
      
      // Animate each item as it loads
      response.data.proformaInvoices.forEach((invoice, index) => {
        setTimeout(() => {
          animateItem(invoice._id, 'slideIn');
        }, index * 100);
      });
      
      showNotification(`Loaded ${response.data.proformaInvoices.length} proforma invoices`, 'success');
    } catch (error) {
      console.error('Error fetching proforma invoices:', error);
      showErrorAlert('Error', 'Failed to load proforma invoices. Please try again.');
    } finally {
      setLoading(false);
      setIsAnimating(false);
    }
  };

  const fetchAvailableQuotations = async () => {
    try {
      setButtonLoadingState('quotation-fetch', true);
      showNotification('Fetching quotations...', 'info');
      
      const response = await api.get('/proforma-invoices/available-quotations', {
        params: {
          company: user.user.company._id || user.user.company
        }
      });
      
      setQuotations(response.data);
      showSuccessAlert('Success!', `Found ${response.data.length} available quotations`);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      showErrorAlert('Error', 'Failed to fetch quotations. Please try again.');
    } finally {
      setButtonLoadingState('quotation-fetch', false);
    }
  };

  const fetchReservedDetails = async (itemId, piId) => {
    try {
      console.log('Fetching reserved details for itemId:', itemId);
      const response = await api.get(`/proforma-invoices/reserved-details/${itemId}`, {
        params: { piId }
      });
      console.log('Reserved details response:', response.data);
      setReservedDetailsModal({ 
        open: true, 
        item: response.data.item, 
        details: response.data.reservedDetails || [] 
      });
    } catch (error) {
      console.error('Error fetching reserved details:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        showErrorAlert('Error', `Failed to fetch reserved details: ${error.response.data.message || error.message}`);
      } else {
        showErrorAlert('Error', 'Failed to fetch reserved details. Please try again.');
      }
    }
  };

  const handleQuotationTypeChange = (type) => {
    setForm({ ...form, quotationType: type, quotation: null, items: [] });
    fetchAvailableQuotations(type);
  };

  const handleQuotationSelect = (quotation) => {
    setForm({ 
      ...form, 
      quotation: quotation._id,
      quotationType: quotation.type 
    });
    // Auto-fetch and import items
    fetchAndImportQuotationItems(quotation._id, quotation.type);
    setQuotationSelectionModal({ open: false, step: 1 });
  };

  const fetchAndImportQuotationItems = async (quotationId, type) => {
    try {
      showNotification('Importing quotation items...', 'info');
      const response = await api.get(`/proforma-invoices/quotation-items/${quotationId}`, {
        params: { type }
      });
      
      console.log('Quotation items response:', response.data);
      
      // Handle new response format with items and additionalCharges
      const { items, additionalCharges } = response.data;
      
      if (!items || items.length === 0) {
        showErrorAlert('No Items', 'No items found in the selected quotation');
        return;
      }
      
      const processedItems = items.map(item => ({
        ...item,
        stockStatus: 'to-be-reserved', // default status
        reservedQuantity: item.reservedQuantity || 0
      }));
      
      // Update form with items and additional charges
      setForm(prev => ({ 
        ...prev, 
        items: processedItems,
        // Apply additional charges for quotation-new, reset for tally-quotation
        cartage: type === 'quotation-new' && additionalCharges ? (additionalCharges.cartage || 0) : 0,
        labour: type === 'quotation-new' && additionalCharges ? (additionalCharges.labour || 0) : 0,
        packingAndForwarding: type === 'quotation-new' && additionalCharges ? (additionalCharges.packingAndForwarding || 0) : 0,
        roundOff: type === 'quotation-new' && additionalCharges ? (additionalCharges.roundOff || 0) : 0,
        discountAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.specialDiscount || 0) : 0,
        taxAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.taxAmount || 0) : 0 // Reset taxAmount for tally quotations
      }));
      
      // Calculate totals with proper additional charges
      const chargesToUse = {
        cartage: type === 'quotation-new' && additionalCharges ? (additionalCharges.cartage || 0) : 0,
        labour: type === 'quotation-new' && additionalCharges ? (additionalCharges.labour || 0) : 0,
        packingAndForwarding: type === 'quotation-new' && additionalCharges ? (additionalCharges.packingAndForwarding || 0) : 0,
        roundOff: type === 'quotation-new' && additionalCharges ? (additionalCharges.roundOff || 0) : 0,
        discountAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.specialDiscount || 0) : 0,
        taxAmount: type === 'quotation-new' && additionalCharges ? (additionalCharges.taxAmount || 0) : 0 // Reset taxAmount for tally quotations
      };
      
      calculateTotals(processedItems, chargesToUse);
      showSuccessAlert('Success!', `Imported ${processedItems.length} items successfully! 🎉`);
    } catch (error) {
      console.error('Error fetching and importing quotation items:', error);
      showErrorAlert('Import Failed', 'Failed to import quotation items. Please try again.');
    }
  };

  const openQuotationSelectionModal = () => {
    setIsAnimating(true);
    showNotification('Opening quotation selection...', 'info');
    fetchAvailableQuotations(); // Fetch all quotations directly
    setQuotationSelectionModal({ open: true, step: 1 });
    
    setTimeout(() => {
      setIsAnimating(false);
      showInfoAlert('Select Quotation', 'Choose from available quotations! 📋');
    }, 500);
  };

  const handleModalQuotationTypeSelect = (type) => {
    setForm({ ...form, quotationType: type, quotation: null, items: [] });
    fetchAvailableQuotations(type);
    setQuotationSelectionModal({ open: true, step: 2 });
  };

  const handleStockStatusChange = (index, stockStatus) => {
    const updatedItems = [...form.items];
    updatedItems[index].stockStatus = stockStatus;
    setForm({ ...form, items: updatedItems });
    
    // Animate the updated item
    animateItem(`item-${index}`, 'pulse');
    showNotification(`Stock status updated to: ${stockStatus}`, 'success');
  };

  const calculateTotals = (items, additionalChargesOverride = null) => {
    const subTotal = items.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // Use override values if provided, otherwise use form values
    const charges = additionalChargesOverride || {
      cartage: form.cartage || 0,
      labour: form.labour || 0,
      packingAndForwarding: form.packingAndForwarding || 0,
      roundOff: form.roundOff || 0,
      discountAmount: form.discountAmount || 0,
      taxAmount: form.taxAmount || 0
    };
    
    const totalAmount = subTotal + charges.taxAmount + charges.cartage + 
                       charges.packingAndForwarding + charges.labour + charges.roundOff - charges.discountAmount;
    const balanceAmount = form.paymentStatus === 'PENDING' ? totalAmount : 
                         Math.max(0, totalAmount - (form.advanceAmount || 0));
    
    setForm(prev => ({
      ...prev,
      subTotal,
      totalAmount,
      balanceAmount,
      // Update the charges in form if override was provided
      ...(additionalChargesOverride ? {
        cartage: charges.cartage,
        labour: charges.labour,
        packingAndForwarding: charges.packingAndForwarding,
        roundOff: charges.roundOff,
        discountAmount: charges.discountAmount,
        taxAmount: charges.taxAmount
      } : {})
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setButtonLoadingState('submit', true);
      
      // Show loading animation
      Swal.fire({
        title: edit ? 'Updating Proforma Invoice...' : 'Creating Proforma Invoice...',
        text: 'Please wait while we process your request',
        icon: 'info',
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      
      // Ensure required fields are included
      const formData = {
        ...form,
        createdBy: user.user._id,
        companyId: user.user.company
      };
      
      console.log('Submitting proforma invoice with data:', formData);
      console.log('User context:', user);
      
      if (edit) {
        await api.put(`/proforma-invoices/${editId}`, formData);
        showSuccessAlert('Success!', 'Proforma invoice updated successfully! 🎉');
      } else {
        await api.post('/proforma-invoices', formData);
        showSuccessAlert('Success!', 'Proforma invoice created successfully! 🎉');
      }
      
      setModalVisible(false);
      resetForm();
      fetchProformaInvoices();
      
      // Celebrate with confetti effect
      setTimeout(() => {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 2000);
      }, 500);
      
    } catch (error) {
      console.error('Error saving proforma invoice:', error);
      
      const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
      showErrorAlert('Error!', `Failed to ${edit ? 'update' : 'create'} proforma invoice: ${errorMessage}`);
      
      // Shake animation for error
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 1000);
    } finally {
      setLoading(false);
      setButtonLoadingState('submit', false);
    }
  };

  const handleComplete = async (id) => {
    try {
      const result = await showWarningAlert(
        'Complete Proforma Invoice',
        'Are you sure you want to mark this proforma invoice as complete? This action cannot be undone.'
      );
      
      if (result.isConfirmed) {
        setButtonLoadingState(`complete-${id}`, true);
        
        await api.patch(`/proforma-invoices/${id}/complete`);
        
        showSuccessAlert('Completed!', 'Proforma invoice marked as complete successfully! ✅');
        animateItem(id, 'bounce');
        fetchProformaInvoices();
      }
    } catch (error) {
      console.error('Error completing proforma invoice:', error);
      showErrorAlert('Error', 'Failed to complete proforma invoice. Please try again.');
    } finally {
      setButtonLoadingState(`complete-${id}`, false);
    }
  };

  const handleCompleteWithLastDeliveryOrder = async (proformaInvoiceId) => {
    try {
      // Fetch assigned delivery orders for this proforma invoice
      const response = await api.get(`/proforma-invoices/${proformaInvoiceId}/assigned-delivery-orders`, {
        params: { userId: user.user._id }
      });
      
      const assignedOrders = response.data || [];
      
      if (assignedOrders.length === 0) {
        showErrorAlert('Error', 'No delivery orders assigned to this proforma invoice. Please assign delivery orders first.');
        return;
      }
      
      if (assignedOrders.length === 1) {
        // If only one delivery order, use it as the last delivery order
        await completeProformaInvoice(proformaInvoiceId, [assignedOrders[0]._id]);
      } else {
        // If multiple delivery orders, show modal to select the last one
        setCompletionModal({
          open: true,
          proformaInvoiceId,
          deliveryOrders: assignedOrders,
          selectedLastDeliveryOrderIds: []
        });
      }
    } catch (error) {
      console.error('Error fetching delivery orders for completion:', error);
      showErrorAlert('Error', 'Failed to fetch delivery orders. Please try again.');
    }
  };

  const completeProformaInvoice = async (proformaInvoiceId, lastDeliveryOrderIds) => {
    try {
      const result = await showWarningAlert(
        'Complete Proforma Invoice',
        'Are you sure you want to mark this proforma invoice as complete? This action cannot be undone.'
      );
      
      if (result.isConfirmed) {
        setButtonLoadingState(`complete-${proformaInvoiceId}`, true);
        
        // Ensure lastDeliveryOrderIds is always an array of IDs
        const idsArray = Array.isArray(lastDeliveryOrderIds)
          ? lastDeliveryOrderIds
          : [lastDeliveryOrderIds].filter(Boolean);
        
        await api.patch(`/proforma-invoices/${proformaInvoiceId}/complete`, {
          lastDeliveryOrderIds: idsArray,
          completedBy: user.user._id
        }, {
          params: {
            userId: user.user._id
          }
        });
        
        showSuccessAlert('Completed!', 'Proforma invoice marked as complete successfully! ✅');
        animateItem(proformaInvoiceId, 'bounce');
        fetchProformaInvoices();
        
        // Close completion modal if open
        setCompletionModal({
          open: false,
          proformaInvoiceId: null,
          deliveryOrders: [],
          selectedLastDeliveryOrderIds: []
        });
      }
    } catch (error) {
      console.error('Error completing proforma invoice:', error);
      showErrorAlert('Error', 'Failed to complete proforma invoice. Please try again.');
    } finally {
      setButtonLoadingState(`complete-${proformaInvoiceId}`, false);
    }
  };

  const handleEdit = (proformaInvoice) => {
    setIsAnimating(true);
    showNotification('Loading proforma invoice for editing...', 'info');
    
    setForm(proformaInvoice);
    setEdit(true);
    setEditId(proformaInvoice._id);
    
    // Pre-fetch quotations for the selected type
    if (proformaInvoice.quotationType) {
      fetchAvailableQuotations(proformaInvoice.quotationType);
    }
    
    setTabValue(1);
    animateItem(proformaInvoice._id, 'highlight');
    
    setTimeout(() => {
      setIsAnimating(false);
      showSuccessAlert('Ready to Edit!', 'Proforma invoice loaded for editing ✏️');
    }, 1000);
  };

  const fetchLinks = async (id) => {
    try {
      const response = await api.get(`/proforma-invoices/${id}/links`);
      if (response.data?.success) {
        setViewLinks({
          indents: response.data.indents || [],
          goodsReceipts: response.data.goodsReceipts || []
        });
      }
    } catch (error) {
      console.error('Error fetching linked documents:', error);
      setViewLinks({ indents: [], goodsReceipts: [] });
    }
  };

  const handleView = (proformaInvoice) => {
    setViewModal({ open: true, proformaInvoice });
    setViewLinks({ indents: [], goodsReceipts: [] });
    fetchLinks(proformaInvoice._id);
    showNotification('Opening proforma invoice details...', 'info');
  };

  const handlePrint = (proformaInvoice) => {
    // Open the ProformaInvoiceReview component in a new tab
    const printUrl = `/proforma-invoice-review/${proformaInvoice._id}`;
    window.open(printUrl, '_blank');
    showNotification('Opening print preview...', 'info');
  };

  const handleUpdateStockStatus = (proformaInvoice) => {
    // Find items that can be updated (status = 'to-be-reserved')
    const eligibleItems = proformaInvoice.items.filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus));
    
    if (eligibleItems.length === 0) {
      showErrorAlert('No Items to Update', 'There are no reserved or goods-received items to update.');
      return;
    }

    setStockStatusModal({ 
      open: true, 
      proformaInvoice, 
      selectedItems: [...eligibleItems.map(item => item._id)] // Pre-select all eligible items
    });
  };

  const handleConfirmStockStatusUpdate = async () => {
    const { proformaInvoice, selectedItems } = stockStatusModal;
    
    if (selectedItems.length === 0) {
      showErrorAlert('No Items Selected', 'Please select at least one item to update.');
      return;
    }

    try {
      setButtonLoadingState(`update-stock-${proformaInvoice._id}`, true);
      
      const response = await api.patch(`/proforma-invoices/${proformaInvoice._id}/update-stock-status`, {
        itemIds: selectedItems
      }, {
        params: {
          userId: user.user._id
        }
      });
      
      showSuccessAlert(
        'Stock Status Updated!', 
        `Successfully updated ${response.data.updatedItemsCount} items to "To Be Billed" status ✅`
      );
      
      setStockStatusModal({ open: false, proformaInvoice: null, selectedItems: [] });
      fetchProformaInvoices(); // Refresh the list
      fetchAttentionSummary();
    } catch (error) {
      console.error('Error updating stock status:', error);
      showErrorAlert('Error', 'Failed to update stock status. Please try again.');
    } finally {
      setButtonLoadingState(`update-stock-${proformaInvoice._id}`, false);
    }
  };

  const handleBillAllReady = async (invoice) => {
    const eligible = (invoice.items || []).filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus));
    if (eligible.length === 0) {
      showErrorAlert('Nothing to Bill', 'No reserved or goods-received items to bill.');
      return;
    }
    const result = await showWarningAlert(
      'Bill All Ready Items',
      `Bill ${eligible.length} item(s) on ${invoice.invoiceNumber}? They become available for delivery orders.`
    );
    if (!result.isConfirmed) return;
    try {
      setButtonLoadingState(`bill-all-${invoice._id}`, true);
      const response = await api.patch(`/proforma-invoices/${invoice._id}/update-stock-status`, {
        itemIds: eligible.map(item => item._id)
      }, { params: { userId: user.user._id } });
      showSuccessAlert('Billed!', `Billed ${response.data.updatedItemsCount} item(s).`);
      fetchProformaInvoices();
      fetchAttentionSummary();
    } catch (error) {
      console.error('Error billing ready items:', error);
      showErrorAlert('Error', 'Failed to bill items. Please try again.');
    } finally {
      setButtonLoadingState(`bill-all-${invoice._id}`, false);
    }
  };

  const fetchAvailableDeliveryOrders = async (proformaInvoiceId) => {
    try {
      showNotification('Loading delivery orders...', 'info');
      
      // Fetch both available and already assigned delivery orders
      const [availableResponse, assignedResponse] = await Promise.all([
        api.get(`/proforma-invoices/${proformaInvoiceId}/available-delivery-orders`, {
          params: { userId: user.user._id }
        }),
        api.get(`/proforma-invoices/${proformaInvoiceId}/assigned-delivery-orders`, {
          params: { userId: user.user._id }
        })
      ]);
      
      setAvailableDeliveryOrders(availableResponse.data);
      setSelectedDeliveryOrders([]); // Reset selection
      
      // Set the modal with both available and assigned orders
      setDeliveryOrderModal({ 
        open: true, 
        proformaInvoiceId,
        assignedOrders: assignedResponse.data || []
      });
      
      showNotification(`Found ${availableResponse.data.length} available delivery orders`, 'success');
    } catch (error) {
      console.error('Error fetching available delivery orders:', error);
      showErrorAlert('Error', 'Failed to fetch available delivery orders');
    }
  };

  const handleAssignDeliveryOrders = async () => {
    try {
      if (selectedDeliveryOrders.length === 0) {
        showErrorAlert('Error', 'Please select at least one delivery order');
        return;
      }

      setButtonLoadingState('assign-delivery-orders', true);
      
      const response = await api.post(`/proforma-invoices/${deliveryOrderModal.proformaInvoiceId}/assign-delivery-orders`, {
        deliveryOrderIds: selectedDeliveryOrders,
        userId: user.user._id
      });

      // Refresh the assigned orders list
      const assignedResponse = await api.get(`/proforma-invoices/${deliveryOrderModal.proformaInvoiceId}/assigned-delivery-orders`, {
        params: { userId: user.user._id }
      });
      
      setDeliveryOrderModal(prev => ({
        ...prev,
        assignedOrders: assignedResponse.data || []
      }));
      
      setSelectedDeliveryOrders([]);
      fetchProformaInvoices(); // Refresh the main list
      
      showSuccessAlert(
        'Success!', 
        `${selectedDeliveryOrders.length} delivery order(s) assigned successfully! Status updated to ${response.data.status}`
      );
      
      // Refresh available orders after assignment
      fetchAvailableDeliveryOrders(deliveryOrderModal.proformaInvoiceId);
    } catch (error) {
      console.error('Error assigning delivery orders:', error);
      showErrorAlert('Error', 'Failed to assign delivery orders');
    } finally {
      setButtonLoadingState('assign-delivery-orders', false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'This proforma invoice will be permanently deleted!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#EF4444',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        customClass: {
          popup: 'animate-pulse'
        }
      });

      if (result.isConfirmed) {
        setButtonLoadingState(`delete-${id}`, true);
        
        // Show deletion progress
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait while we delete the proforma invoice',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
        
        await api.delete(`/proforma-invoices/${id}`);
        
        // Animate item removal
        animateItem(id, 'fadeOut');
        
        setTimeout(() => {
          fetchProformaInvoices();
          showSuccessAlert('Deleted!', 'Proforma invoice has been deleted successfully! 🗑️');
        }, 1000);
      }
    } catch (error) {
      console.error('Error deleting proforma invoice:', error);
      const msg = error.response?.data?.message || 'Failed to delete proforma invoice. Please try again.';
      showErrorAlert('Cannot Delete', msg);
    } finally {
      setButtonLoadingState(`delete-${id}`, false);
    }
  };

  const resetForm = () => {
    setForm({
      quotationType: '',
      quotation: null,
      daysRequiredForDelivery: 1,
      items: [],
      paymentStatus: 'TOTAL_CREDIT',
      advanceAmount: 0,
      balanceAmount: 0,
      totalAmount: 0,
      subTotal: 0,
      discountAmount: 0,
      taxAmount: 0,
      cartage: 0,
      packingAndForwarding: 0,
      labour: 0,
      roundOff: 0,
      remarks: ''
    });
    setEdit(false);
    setEditId(null);
    setQuotationSelectionModal({ open: false, step: 1 });
    setDeliveryOrderModal({ open: false, proformaInvoiceId: null, assignedOrders: [] });
    setQuotations([]);
    setSelectedDeliveryOrders([]);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const isOverdue = (invoice) => {
    if (!invoice?.expectedDeliveryDate) return false;
    if (invoice.status === 'COMPLETED' || invoice.status === 'CANCELLED') return false;
    return new Date(invoice.expectedDeliveryDate) < new Date();
  };

  const billableCount = (invoice) =>
    (invoice?.items || []).filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus)).length;

  const buildProgress = (invoice) => {
    if (!invoice) return [];
    const items = invoice.items || [];
    const dos = invoice.deliveryOrders || [];
    const receivedDates = items
      .map(i => i.goodsReceiptDetails?.receiptDate)
      .filter(Boolean)
      .sort();
    const delivered = dos
      .filter(d => d.status === 'Completed')
      .sort((a, b) => new Date(b.completedAt || b.updatedAt || b.createdAt) - new Date(a.completedAt || a.updatedAt || a.createdAt))[0];
    const allBilled = items.length > 0 && items.every(i => i.stockStatus === 'to-be-billed');
    const allDelivered = dos.length > 0 && dos.every(d => d.status === 'Completed');

    return [
      { label: 'Created', done: true, date: invoice.invoiceDate || invoice.createdAt },
      { label: 'Ordered', done: items.some(i => ['to-be-ordered', 'goods-received', 'to-be-billed'].includes(i.stockStatus)), date: null },
      { label: 'Received', done: receivedDates.length > 0, date: receivedDates[0] || null },
      { label: 'Billed', done: allBilled, date: null },
      { label: 'Delivered', done: allDelivered, date: delivered ? (delivered.completedAt || delivered.createdAt) : null }
    ];
  };

  const handlePaymentStatusChange = (paymentStatus) => {
    let advanceAmount = 0;
    
    if (paymentStatus === 'FULL_PAID') {
      // Full paid: advance amount = total amount (disabled)
      advanceAmount = form.totalAmount;
    } else if (paymentStatus === 'TOTAL_CREDIT') {
      // Total credit: advance amount = 0 (disabled)
      advanceAmount = 0;
    } else if (paymentStatus === 'ADVANCE_PARTIAL') {
      // Partial payment: keep current advance amount or set reasonable default (editable)
      advanceAmount = form.advanceAmount || 0;
    }
    
    const balanceAmount = Math.max(0, form.totalAmount - advanceAmount);
    
    setForm(prev => ({
      ...prev,
      paymentStatus,
      advanceAmount,
      balanceAmount
    }));
  };

  // Keyboard event handler
  const handleKeyDown = useCallback((e) => {
    // Global shortcuts (work anywhere)
    if (e.ctrlKey || e.metaKey) {
      switch (e.key) {
        case 'n':
          e.preventDefault();
          if (!modalVisible) {
            setTabValue(1);
            setTimeout(() => quotationTypeRef.current?.focus(), 100);
          }
          break;
        case 's':
          e.preventDefault();
          if (modalVisible && (form.quotation && form.items.length > 0)) {
            handleSubmit();
          }
          break;
        case 'Escape':
        case 'w':
          e.preventDefault();
          if (modalVisible) {
            setTabValue(0);
            resetForm();
          }
          break;
        case '/':
          e.preventDefault();
          searchRef.current?.focus();
          break;
        case '?':
          e.preventDefault();
          setKeyboardHelp(!keyboardHelp);
          break;
      }
    }

    // Form navigation (Tab/Shift+Tab for better UX)
    if (modalVisible && e.key === 'Tab') {
      // Let browser handle normal tab navigation
      return;
    }

    // Pagination shortcuts
    if (!modalVisible && !keyboardHelp) {
      if (e.key === 'ArrowLeft' && page > 1) {
        e.preventDefault();
        setPage(prev => prev - 1);
      }
      if (e.key === 'ArrowRight' && page < totalPages) {
        e.preventDefault();
        setPage(prev => prev + 1);
      }
    }

    // Close modals with Escape
    if (e.key === 'Escape') {
      if (keyboardHelp) {
        setKeyboardHelp(false);
      } else if (quotationSelectionModal.open) {
        setQuotationSelectionModal({ open: false, step: 1 });
      }
    }
  }, [modalVisible, form, page, totalPages, keyboardHelp, quotationSelectionModal]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Dark mode class map
  const dc = useMemo(() => ({
    page: isDarkMode
      ? 'min-h-screen bg-gray-900 text-gray-100'
      : 'min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100',
    header: isDarkMode
      ? 'bg-gray-800 shadow-lg border-b border-gray-700'
      : 'bg-white shadow-lg border-b border-gray-200',
    headerTitle: isDarkMode
      ? 'text-3xl font-bold bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent'
      : 'text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent',
    headerSubtitle: isDarkMode ? 'mt-1 text-sm text-gray-400' : 'mt-1 text-sm text-gray-600',
    card: isDarkMode
      ? 'bg-gray-800 rounded-xl shadow-lg border border-gray-700'
      : 'bg-white rounded-xl shadow-lg border border-gray-200',
    cardInner: isDarkMode
      ? 'bg-gray-800 rounded-xl shadow-sm border border-gray-700 p-1'
      : 'bg-white rounded-xl shadow-sm border border-gray-200 p-1',
    tabActive: isDarkMode
      ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md transform scale-105'
      : 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md transform scale-105',
    tabInactive: isDarkMode
      ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50',
    filterCard: isDarkMode
      ? 'bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-700 mb-8'
      : 'bg-white p-6 rounded-xl shadow-lg border border-gray-200 mb-8',
    filterLabel: isDarkMode ? 'block text-sm font-medium text-gray-300 mb-2' : 'block text-sm font-medium text-gray-700 mb-2',
    filterInput: isDarkMode
      ? 'w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors placeholder-gray-400'
      : 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors',
    filterSelect: isDarkMode
      ? 'w-full px-4 py-2 border border-gray-600 rounded-lg bg-gray-700 text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors'
      : 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white',
    filterClearBtn: isDarkMode
      ? 'w-full px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors'
      : 'w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors',
    tableContainer: isDarkMode
      ? 'bg-gray-800 rounded-xl shadow-lg border border-gray-700 overflow-hidden'
      : 'bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden',
    tableHeaderBar: isDarkMode
      ? 'px-6 py-4 border-b border-gray-700 bg-gray-800'
      : 'px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100',
    tableHead: isDarkMode
      ? 'bg-gray-700'
      : 'bg-gradient-to-r from-gray-50 to-gray-100',
    tableTh: isDarkMode
      ? 'px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider'
      : 'px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider',
    tableRow: isDarkMode
      ? 'hover:bg-gray-700/50 transition-all duration-300'
      : 'hover:bg-gray-50 transition-all duration-300',
    tableTd: isDarkMode
      ? 'px-6 py-4 whitespace-nowrap text-sm text-gray-300'
      : 'px-6 py-4 whitespace-nowrap text-sm text-gray-900',
    tableTdBold: isDarkMode
      ? 'px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-100'
      : 'px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900',
    tableEmpty: isDarkMode ? 'px-6 py-4 text-center text-gray-400' : 'px-6 py-4 text-center text-gray-500',
    paginationBtn: isDarkMode
      ? 'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
      : 'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
    paginationBtnActive: isDarkMode
      ? 'z-10 bg-blue-900/50 border-blue-500 text-blue-400'
      : 'z-10 bg-blue-50 border-blue-500 text-blue-600',
    paginationBtnInactive: isDarkMode
      ? 'bg-gray-800 border-gray-600 text-gray-400 hover:bg-gray-700'
      : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
    text: isDarkMode ? 'text-gray-100' : 'text-gray-900',
    textMuted: isDarkMode ? 'text-gray-400' : 'text-gray-600',
    textMutedSm: isDarkMode ? 'text-sm text-gray-400' : 'text-sm text-gray-600',
    textMutedXs: isDarkMode ? 'text-xs text-gray-500' : 'text-xs text-gray-500',
    createCard: isDarkMode
      ? 'bg-gray-800 rounded-xl shadow-lg border border-gray-700'
      : 'bg-white rounded-xl shadow-lg border border-gray-200',
    createHeader: isDarkMode
      ? 'px-8 py-6 border-b border-gray-700 bg-gradient-to-r from-blue-900/30 to-indigo-900/30'
      : 'px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50',
    stepBox: (color) => isDarkMode
      ? `bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-${color}-500 transition-colors`
      : `bg-gradient-to-r from-${color}-50 to-${color === 'gray' ? 'slate' : color === 'orange' ? 'amber' : color === 'green' ? 'emerald' : color === 'purple' ? 'pink' : color}-50 border-2 border-dashed border-${color}-300 rounded-xl p-6 hover:border-${color}-400 transition-colors`,
    itemCard: isDarkMode
      ? 'bg-gray-700 rounded-lg shadow-sm border border-gray-600 hover:shadow-md transition-shadow duration-200'
      : 'bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-200',
    itemStatBox: isDarkMode
      ? 'bg-gray-800 rounded-lg p-3'
      : 'bg-gray-50 rounded-lg p-3',
    itemStatLabel: isDarkMode
      ? 'text-xs font-medium text-gray-400 uppercase tracking-wider mb-1'
      : 'text-xs font-medium text-gray-500 uppercase tracking-wider mb-1',
    chargeInput: isDarkMode
      ? 'bg-gray-700 rounded-lg shadow-sm border border-gray-600 p-4'
      : 'bg-white rounded-lg shadow-sm border border-gray-200 p-4',
    summaryCard: isDarkMode
      ? 'bg-gray-700 rounded-lg shadow-sm border border-gray-600 p-4'
      : 'bg-white rounded-lg shadow-sm border border-gray-200 p-4',
    modalOverlay: 'fixed inset-0 z-50 bg-black/60 overflow-y-auto h-full w-full backdrop-blur-sm',
    modalCard: isDarkMode
      ? 'relative top-10 mx-auto p-6 border border-gray-700 shadow-2xl rounded-xl bg-gray-800'
      : 'relative top-10 mx-auto p-6 border border-gray-200 shadow-2xl rounded-xl bg-white',
    modalTitle: isDarkMode ? 'text-xl font-semibold text-gray-100' : 'text-xl font-semibold text-gray-900',
    modalBg: isDarkMode ? 'bg-gray-800' : 'bg-white',
    modalSubBg: isDarkMode ? 'bg-gray-700' : 'bg-gray-50',
    badge: (color) => isDarkMode
      ? `inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-${color}-900/40 text-${color}-300 border border-${color}-700/50`
      : `inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-${color}-100 text-${color}-800`,
    quotationCard: isDarkMode
      ? 'bg-gray-700 border border-gray-600 rounded-xl p-4 hover:border-blue-500 hover:bg-gray-600/50 transition-all duration-200 cursor-pointer group'
      : 'bg-white border border-gray-200 rounded-xl p-4 hover:border-blue-400 hover:shadow-md transition-all duration-200 cursor-pointer group',
    quotationCardSelected: isDarkMode
      ? 'bg-blue-900/30 border-2 border-blue-500 rounded-xl p-4'
      : 'bg-blue-50 border-2 border-blue-400 rounded-xl p-4',
  }), [isDarkMode]);

  return (
    <div className={`${dc.page} ${isDarkMode ? 'pi-dark' : ''}`}>
      {/* Header with enhanced styling */}
      <div className={dc.header}>
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className={dc.headerTitle}>
                Proforma Invoice Management
              </h1>
              <p className={dc.headerSubtitle}>
                Create and manage proforma invoices efficiently
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setKeyboardHelp(true)}
                className={`p-2 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                title="Keyboard shortcuts (Ctrl+?)"
              >
                <span className="text-sm">Ctrl+?</span>
              </button>
              <button
                ref={newButtonRef}
                onClick={() => {
                  setTabValue(1);
                  setIsAnimating(true);
                  showNotification('Opening new proforma invoice form...', 'info');
                  setTimeout(() => {
                    quotationTypeRef.current?.focus();
                    setIsAnimating(false);
                    showSuccessAlert('Ready to Create!', 'Fill in the details to create a new proforma invoice ✨');
                  }, 1000);
                }}
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl animate-pulse"
                title="Create new (Ctrl+N)"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Invoice
                <SparklesIcon className="h-4 w-4 ml-2" />
                <span className="ml-2 text-xs opacity-75">Ctrl+N</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Enhanced Tabs */}
        <div className="mb-8">
          <div className={dc.cardInner}>
            <nav className="flex space-x-1">
              <button
                onClick={() => setTabValue(0)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 ${
                  tabValue === 0 ? dc.tabActive : dc.tabInactive
                }`}>
                <div className="flex items-center justify-center">
                  <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
                  Proforma Invoices
                </div>
              </button>
              <button
                onClick={() => setTabValue(1)}
                className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-200 ${
                  tabValue === 1 ? dc.tabActive : dc.tabInactive
                }`}>
                <div className="flex items-center justify-center">
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Create New
                </div>
              </button>
            </nav>
          </div>
        </div>

        {tabValue === 0 && (
        <div>
          {/* Enhanced Filters */}
          <div className={dc.filterCard}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-lg font-semibold ${dc.text}`}>Filters & Search</h3>
              <span className={dc.textMutedSm}>Use arrow keys ← → for pagination</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <label className={dc.filterLabel}>Search</label>
                <input
                  ref={searchRef}
                  type="text"
                  placeholder="Search invoices... (Ctrl+/)"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className={dc.filterInput}
                />
              </div>
              <div>
                <label className={dc.filterLabel}>Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className={dc.filterSelect}>
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div>
                <label className={dc.filterLabel}>Payment Status</label>
                <select
                  value={filters.paymentStatus}
                  onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })}
                  className={dc.filterSelect}>
                  <option value="">All Payments</option>
                  <option value="TOTAL_CREDIT">Total Credit</option>
                  <option value="ADVANCE_PARTIAL">Advance (Partial)</option>
                  <option value="FULL_PAID">Full Paid</option>
                </select>
              </div>
              <div>
                <label className={dc.filterLabel}>Delivery Status</label>
                <select
                  value={filters.deliveryStatus}
                  onChange={(e) => setFilters({ ...filters, deliveryStatus: e.target.value })}
                  className={dc.filterSelect}>
                  <option value="">All Deliveries</option>
                  <option value="PENDING">Pending</option>
                  <option value="PARTIAL">Partial</option>
                  <option value="COMPLETED">Completed</option>
                </select>
              </div>
              <div>
                <label className={dc.filterLabel}>Salesperson</label>
                <select
                  value={filters.salesman}
                  onChange={(e) => setFilters({ ...filters, salesman: e.target.value })}
                  className={dc.filterSelect}>
                  <option value="">All Salespersons</option>
                  {salesmenList.map(s => (
                    <option key={s._id} value={s._id}>{s.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={dc.filterLabel}>Quick Actions</label>
                <button
                  onClick={() => setFilters({ status: '', paymentStatus: '', deliveryStatus: '', salesman: '', search: '', itemStockStatus: '', overdue: false })}
                  className={dc.filterClearBtn}>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>

          {/* Needs Attention */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { key: 'to-be-ordered', label: 'To be Ordered', kind: 'stock' },
              { key: 'goods-received', label: 'Goods Received (unbilled)', kind: 'stock' },
              { key: 'to-be-billed', label: 'To be Billed', kind: 'stock' },
              { key: 'overdue', label: 'Overdue', kind: 'overdue' }
            ].map(card => {
              const active = card.kind === 'overdue' ? !!filters.overdue : filters.itemStockStatus === card.key;
              const onClick = () => setFilters(prev => card.kind === 'overdue'
                ? { ...prev, overdue: !prev.overdue, itemStockStatus: '' }
                : { ...prev, itemStockStatus: active ? '' : card.key, overdue: false });
              return (
                <button
                  key={card.key}
                  type="button"
                  onClick={onClick}
                  className={`text-left rounded-xl border p-4 transition-all ${
                    active
                      ? card.kind === 'overdue'
                        ? 'border-red-500 ring-2 ring-red-200 bg-red-50'
                        : 'border-blue-500 ring-2 ring-blue-200 bg-blue-50'
                      : isDarkMode ? 'border-gray-700 bg-gray-800 hover:border-gray-600' : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <p className={`text-xs font-medium uppercase tracking-wide ${dc.textMuted}`}>{card.label}</p>
                  <p className={`text-2xl font-bold ${card.kind === 'overdue' && (attention[card.key] || 0) > 0 ? 'text-red-600' : dc.text}`}>{attention[card.key] || 0}</p>
                  <p className={`text-xs ${dc.textMuted}`}>{active ? 'Showing only these · click to clear' : 'Click to filter'}</p>
                </button>
              );
            })}
          </div>

          {/* Enhanced Table */}
          <div className={dc.tableContainer}>
            <div className={dc.tableHeaderBar}>
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${dc.text}`}>Proforma Invoices</h3>
                <div className="flex items-center space-x-4">
                  {/* View Mode Toggle */}
                  <div className={`flex rounded-lg p-0.5 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <button
                      onClick={() => setViewMode('table')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        viewMode === 'table'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Table View">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" /></svg>
                    </button>
                    <button
                      onClick={() => setViewMode('cards')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                        viewMode === 'cards'
                          ? 'bg-blue-600 text-white shadow-sm'
                          : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}
                      title="Card View">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>
                    </button>
                  </div>
                  <span className={dc.textMutedSm}>
                    Page {page} of {totalPages}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setPage(prev => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      className={`p-2 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      title="Previous page (←)">
                      <ArrowLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      className={`p-2 ${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
                      title="Next page (→)">
                      <ArrowRightIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading / Empty State */}
            {loading ? (
              <InlineLoader message="Loading invoices…" isDarkMode={isDarkMode} />
            ) : proformaInvoices.length === 0 ? (
              <EmptyState
                icon={DocumentDuplicateIcon}
                title="No proforma invoices"
                message="Create a proforma invoice from a completed quotation to get started."
                isDarkMode={isDarkMode}
              />
            ) : viewMode === 'cards' ? (
              /* Card View */
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {proformaInvoices.map((invoice) => (
                  <div
                    key={invoice._id}
                    className={`${isDarkMode ? 'bg-gray-700 border-gray-600 hover:border-blue-500' : 'bg-white border-gray-200 hover:border-blue-400 hover:shadow-md'} border rounded-xl p-5 transition-all duration-200 ${
                      itemAnimations[invoice._id] ? `animate-${itemAnimations[invoice._id]}` : ''
                    }`}>
                    {/* Card Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-bold ${dc.text}`}>{invoice.invoiceNumber}</p>
                        <p className={`text-sm ${dc.text} truncate mt-0.5`}>
                          {invoice.quotation?.recipient?.name || invoice.quotation?.recipient?.businessName || 'N/A'}
                        </p>
                      </div>
                      <span className={`ml-2 inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                        invoice.quotationType === 'quotation-new'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-emerald-100 text-emerald-700'
                      }`}>
                        {invoice.quotationType === 'quotation-new' ? 'QTN' : 'TALLY'}
                      </span>
                    </div>

                    {/* Financial Summary */}
                    <div className="mb-3">
                      <p className="text-lg font-bold text-green-500">{formatCurrency(invoice.totalAmount)}</p>
                      <div className={`flex items-center gap-3 mt-1 ${dc.textMutedXs}`}>
                        <span>Sub: {formatCurrency(invoice.subTotal || 0)}</span>
                        <span className="text-yellow-500">Adv: {formatCurrency(invoice.advanceAmount || 0)}</span>
                        <span className="text-red-500">Bal: {formatCurrency(invoice.balanceAmount || 0)}</span>
                      </div>
                    </div>

                    {/* Status Badges */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      <StatusBadge status={invoice.status} />
                      <StatusBadge status={invoice.paymentStatus} />
                      <StatusBadge status={invoice.deliveryStatus || 'PENDING'} />
                    </div>

                    {/* Created By / Salesperson */}
                    <div className={`${dc.textMutedXs} mb-2`}>
                      <div className="flex items-center gap-1">
                        <span className={`font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>QTN:</span>
                        <span>{invoice.quotation?.createdBy?.name || 'N/A'}</span>
                        <span className="mx-1">|</span>
                        <span className={`font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>PI:</span>
                        <span>{invoice.createdBy?.name || 'N/A'}</span>
                      </div>
                      {invoice.quotation?.salesmen && invoice.quotation.salesmen.length > 0 && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className={`font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>SP:</span>
                          <span>{invoice.quotation.salesmen.map(s => s.name).join(', ')}</span>
                        </div>
                      )}
                    </div>

                    {/* Meta */}
                    <div className={dc.textMutedXs + ' mb-3 flex items-center gap-2'}>
                      <span>Expected: {new Date(invoice.expectedDeliveryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      {isOverdue(invoice) && (
                        <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold bg-red-100 text-red-700 rounded">Overdue</span>
                      )}
                    </div>

                    {/* Card Actions */}
                    <div className={`flex items-center gap-2 pt-3 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-100'}`}>
                      <button onClick={() => handleView(invoice)} className="text-blue-600 hover:text-blue-800 transition-colors" title="View">
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button onClick={() => handlePrint(invoice)} className="text-purple-600 hover:text-purple-800 transition-colors" title="Print">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                        </svg>
                      </button>
                      {(invoice.status === 'PENDING' || invoice.status === 'PARTIAL') && (
                        <>
                          {billableCount(invoice) > 0 && (
                            <button
                              onClick={() => handleBillAllReady(invoice)}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors ${
                                buttonLoading[`bill-all-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title="Bill all ready items"
                              disabled={buttonLoading[`bill-all-${invoice._id}`]}>
                              {buttonLoading[`bill-all-${invoice._id}`] ? (
                                <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1"></div>
                              ) : (
                                <SparklesIcon className="h-3 w-3 mr-1" />
                              )}
                              Bill {billableCount(invoice)}
                            </button>
                          )}
                          <button
                            onClick={() => handleUpdateStockStatus(invoice)}
                            className={`inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded transition-colors ${
                              buttonLoading[`update-stock-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                            title="Update Stock Status"
                            disabled={buttonLoading[`update-stock-${invoice._id}`]}>
                            {buttonLoading[`update-stock-${invoice._id}`] ? (
                              <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1"></div>
                            ) : (
                              <SparklesIcon className="h-3 w-3 mr-1" />
                            )}
                            Stock
                          </button>
                          <button
                            onClick={() => fetchAvailableDeliveryOrders(invoice._id)}
                            className="text-green-600 hover:text-green-800 transition-colors"
                            title="Assign Delivery Orders">
                            <DocumentDuplicateIcon className="h-5 w-5" />
                          </button>
                          {(invoice.deliveryStatus === 'PARTIAL' || (invoice.deliveryOrders && invoice.deliveryOrders.length > 0)) && (
                            <button
                              onClick={() => handleCompleteWithLastDeliveryOrder(invoice._id)}
                              className={`text-purple-600 hover:text-purple-800 transition-colors ${buttonLoading[`complete-${invoice._id}`] ? 'opacity-50' : ''}`}
                              title="Complete"
                              disabled={buttonLoading[`complete-${invoice._id}`]}>
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                          )}
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(invoice._id)}
                        className={`text-red-500 hover:text-red-700 transition-colors ml-auto ${buttonLoading[`delete-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                        title="Delete Proforma Invoice"
                        disabled={buttonLoading[`delete-${invoice._id}`]}>
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* Table View */
              <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={dc.tableHead}>
                  <tr>
                    <th className={dc.tableTh}>Invoice Number</th>
                    <th className={dc.tableTh}>Recipient / Type</th>
                    <th className={dc.tableTh}>Created By / Salesperson</th>
                    <th className={dc.tableTh}>Financial Summary</th>
                    <th className={dc.tableTh}>Status</th>
                    <th className={dc.tableTh}>Delivery Status</th>
                    <th className={dc.tableTh}>Expected Delivery</th>
                    <th className={`${dc.tableTh} text-center`}>Actions</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {proformaInvoices.map((invoice) => (
                    <tr 
                      key={invoice._id} 
                      className={`${dc.tableRow} transform hover:scale-[1.01] ${
                        itemAnimations[invoice._id] ? `animate-${itemAnimations[invoice._id]}` : ''
                      }`}>
                      <td className={dc.tableTdBold}>
                        {invoice.invoiceNumber}
                      </td>
                      <td className={dc.tableTd}>
                        <div className="flex flex-col">
                          <span className={`text-sm font-medium ${dc.text} truncate max-w-[200px]`}>
                            {invoice.quotation?.recipient?.name || invoice.quotation?.recipient?.businessName || 'N/A'}
                          </span>
                          <span className={`inline-flex w-fit px-2 py-0.5 mt-1 text-[10px] font-bold uppercase rounded ${
                            invoice.quotationType === 'quotation-new'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {invoice.quotationType === 'quotation-new' ? 'Quotation (new)' : 'Tally Quotation'}
                          </span>
                        </div>
                      </td>
                      <td className={dc.tableTd}>
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1">
                            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>QTN:</span>
                            <span className={`text-xs ${dc.text}`}>{invoice.quotation?.createdBy?.name || 'N/A'}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`text-[10px] font-medium ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>PI:</span>
                            <span className={`text-xs ${dc.text}`}>{invoice.createdBy?.name || 'N/A'}</span>
                          </div>
                          {invoice.quotation?.salesmen && invoice.quotation.salesmen.length > 0 && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <span className={`text-[10px] font-medium ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>SP:</span>
                              <span className={`text-xs ${dc.text}`}>
                                {invoice.quotation.salesmen.map(s => s.name).join(', ')}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className={dc.tableTd}>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm font-bold text-green-500">{formatCurrency(invoice.totalAmount)}</span>
                          <div className={`flex items-center gap-2 ${dc.textMutedXs}`}>
                            <span>Sub: {formatCurrency(invoice.subTotal || 0)}</span>
                          </div>
                          <div className={`flex items-center gap-2 ${dc.textMutedXs}`}>
                            <span className="text-yellow-500">Adv: {formatCurrency(invoice.advanceAmount || 0)}</span>
                            <span className="text-red-500">Bal: {formatCurrency(invoice.balanceAmount || 0)}</span>
                          </div>
                          <span className={`inline-flex w-fit mt-0.5`}>
                            <StatusBadge status={invoice.paymentStatus} />
                          </span>
                        </div>
                      </td>
                      <td className={dc.tableTd}>
                        <StatusBadge status={invoice.status} />
                      </td>
                      <td className={dc.tableTd}>
                        <StatusBadge status={invoice.deliveryStatus || 'PENDING'} />
                      </td>
                      <td className={dc.tableTd}>
                        <div className="flex items-center gap-2">
                          <span>{new Date(invoice.expectedDeliveryDate).toLocaleDateString()}</span>
                          {isOverdue(invoice) && (
                            <span className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold bg-red-100 text-red-700 rounded">Overdue</span>
                          )}
                        </div>
                      </td>
                      <td className={`${dc.tableTd} font-medium`}>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleView(invoice)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="View Details"
                          >
                            <EyeIcon className="h-5 w-5" />
                          </button>
                          
                          {/* Print Button */}
                          <button
                            onClick={() => handlePrint(invoice)}
                            className="text-purple-600 hover:text-purple-900 transition-colors"
                            title="Print Invoice"
                          >
                            <svg 
                              className="h-5 w-5" 
                              fill="none" 
                              viewBox="0 0 24 24" 
                              stroke="currentColor"
                            >
                              <path 
                                strokeLinecap="round" 
                                strokeLinejoin="round" 
                                strokeWidth={2} 
                                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" 
                              />
                            </svg>
                          </button>
                          
                          {/* Quick: bill every ready item, then fine-grained Update Stock */}
                          {(invoice.status === 'PENDING' || invoice.status === 'PARTIAL') && billableCount(invoice) > 0 && (
                            <button
                              onClick={() => handleBillAllReady(invoice)}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded transition-colors ${
                                buttonLoading[`bill-all-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title="Bill all ready items"
                              disabled={buttonLoading[`bill-all-${invoice._id}`]}
                            >
                              {buttonLoading[`bill-all-${invoice._id}`] ? (
                                <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1"></div>
                              ) : (
                                <SparklesIcon className="h-3 w-3 mr-1" />
                              )}
                              Bill {billableCount(invoice)}
                            </button>
                          )}
                          {(invoice.status === 'PENDING' || invoice.status === 'PARTIAL') && (
                            <button
                              onClick={() => handleUpdateStockStatus(invoice)}
                              className={`inline-flex items-center px-2 py-1 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded transition-colors ${
                                buttonLoading[`update-stock-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                              title="Update Stock Status (Reserved / Goods Received → To Be Billed)"
                              disabled={buttonLoading[`update-stock-${invoice._id}`]}
                            >
                              {buttonLoading[`update-stock-${invoice._id}`] ? (
                                <div className="animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1"></div>
                              ) : (
                                <SparklesIcon className="h-3 w-3 mr-1" />
                              )}
                              Update Stock
                            </button>
                          )}

                          {(invoice.status === 'PENDING' || invoice.status === 'PARTIAL') && (
                            <>
                              <button
                                onClick={() => fetchAvailableDeliveryOrders(invoice._id)}
                                className="text-green-600 hover:text-green-900 transition-colors mr-2"
                                title="Assign Delivery Orders"
                              >
                                <DocumentDuplicateIcon className="h-5 w-5" />
                              </button>
                              {(invoice.deliveryStatus === 'PARTIAL' || 
                                (invoice.deliveryOrders && invoice.deliveryOrders.length > 0)) && (
                                <button
                                  onClick={() => handleCompleteWithLastDeliveryOrder(invoice._id)}
                                  className={`text-purple-600 hover:text-purple-900 transition-all duration-200 transform hover:scale-110 ${
                                    buttonLoading[`complete-${invoice._id}`] ? 'button-loading' : ''
                                  }`}
                                  title="Complete"
                                  disabled={buttonLoading[`complete-${invoice._id}`]}
                                >
                                  {buttonLoading[`complete-${invoice._id}`] ? (
                                    <div className="h-5 w-5"></div>
                                  ) : (
                                    <CheckCircleIcon className="h-5 w-5" />
                                  )}
                                </button>
                              )}
                            </>
                          )}
                          {(invoice.status !== 'PENDING' && invoice.status !== 'PARTIAL') && (
                            <span className={isDarkMode ? 'text-gray-500 text-sm' : 'text-gray-400 text-sm'}>No actions available</span>
                          )}
                          <button
                            onClick={() => handleDelete(invoice._id)}
                            className={`text-red-500 hover:text-red-700 transition-colors ${buttonLoading[`delete-${invoice._id}`] ? 'opacity-50 cursor-not-allowed' : ''}`}
                            title="Delete Proforma Invoice"
                            disabled={buttonLoading[`delete-${invoice._id}`]}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
            )}

          {/* Pagination */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            limit={limit}
            onLimitChange={setLimit}
            totalItems={total}
            isDarkMode={isDarkMode}
          />
          </div>
        </div>
      )}

      {tabValue === 1 && (
        <div className={dc.createCard}>
          <div className={dc.createHeader}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className={`text-2xl font-bold ${dc.text}`}>Create New Proforma Invoice</h2>
                <p className={`${dc.textMuted} mt-1`}>Fill in the details below to create a new proforma invoice</p>
              </div>
              <div className={`text-sm ${dc.textMuted} space-y-1`}>
                <div>Save: <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+S</kbd></div>
                <div>Cancel: <kbd className="px-1 py-0.5 bg-gray-200 rounded text-xs">Ctrl+W</kbd></div>
              </div>
            </div>
          </div>
          
          <form 
            ref={formRef} 
            className="p-8 space-y-8"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            {/* Step 1: Enhanced Quotation Selection */}
            <div className={isDarkMode ? 'bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-blue-500 transition-colors' : 'bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-blue-400 transition-colors'}>
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                  1
                </div>
                <h3 className={`ml-3 text-lg font-semibold ${dc.text}`}>
                  Select Quotation
                </h3>
              </div>
              
              {!form.quotation ? (
                <div className="text-center py-12">
                  <div className={dc.textMuted + ' mb-6'}>
                    <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h4 className={`text-lg font-medium ${dc.text} mb-2`}>No quotation selected</h4>
                  <p className={`${dc.textMuted} mb-6`}>Choose a quotation to get started with your proforma invoice</p>
                  <p className={`${dc.textMuted} mb-4`}>
                    No quotation selected. Please select a quotation to proceed.
                  </p>
                  <button
                    type="button"
                    onClick={openQuotationSelectionModal}
                    className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Select Quotation
                  </button>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Quotation Selected
                      </p>
                      <p className="text-sm text-green-600">
                        Type: {form.quotationType === 'quotation-new' ? 'Quotation (new)' : 'Tally Quotation'}
                      </p>
                      <p className="text-sm text-green-600">
                        Items: {form.items.length} item(s) imported
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={openQuotationSelectionModal}
                      className="text-green-600 hover:text-green-800"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Show rest of the form only if quotation is selected */}
            {form.quotation && (
              <>
                {/* Step 2: Enhanced Delivery & Payment Details */}
                <div className={isDarkMode ? 'bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-orange-500 transition-colors' : 'bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-dashed border-orange-300 rounded-xl p-6 hover:border-orange-400 transition-colors'}>
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      2
                    </div>
                    <h3 className={`ml-3 text-lg font-semibold ${dc.text}`}>
                      Delivery & Payment Details
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {/* Days Required for Delivery */}
                    <div className={dc.chargeInput}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center mr-2">
                            <span className="text-orange-600 text-xs font-semibold">📅</span>
                          </div>
                          Days Required for Delivery
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          value={form.daysRequiredForDelivery}
                          onChange={(e) => setForm({ ...form, daysRequiredForDelivery: parseInt(e.target.value) })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-center text-lg font-semibold"
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 text-sm">days</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Status */}
                    <div className={dc.chargeInput}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2">
                            <CurrencyRupeeIcon className="h-3 w-3 text-green-600" />
                          </div>
                          Payment Status
                        </div>
                      </label>
                      <div className="relative">
                        <select
                          value={form.paymentStatus}
                          onChange={(e) => handlePaymentStatusChange(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors text-center text-lg font-semibold appearance-none bg-white"
                        >
                          {paymentStatusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <div className="mt-2 text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          form.paymentStatus === 'FULL_PAID' ? 'bg-green-100 text-green-800' :
                          form.paymentStatus === 'ADVANCE_PARTIAL' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {form.paymentStatus === 'FULL_PAID' ? '✅ Fully Paid' :
                           form.paymentStatus === 'ADVANCE_PARTIAL' ? '⚠️ Partial Payment' :
                           '❌ Total Credit'}
                        </span>
                      </div>
                    </div>

                    {/* Advance Amount - Always show but with conditional styling */}
                    <div className={`${dc.chargeInput} ${
                      form.paymentStatus === 'TOTAL_CREDIT' || form.paymentStatus === 'FULL_PAID' ? 'opacity-75' : ''
                    }`}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                            <CurrencyRupeeIcon className="h-3 w-3 text-blue-600" />
                          </div>
                          Advance Amount
                        </div>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          value={form.advanceAmount}
                          onChange={(e) => {
                            const advanceAmount = parseFloat(e.target.value) || 0;
                            const balanceAmount = Math.max(0, form.totalAmount - advanceAmount);
                            setForm({ ...form, advanceAmount, balanceAmount });
                          }}
                          disabled={form.paymentStatus === 'TOTAL_CREDIT' || form.paymentStatus === 'FULL_PAID'}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-center text-lg font-semibold disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                          placeholder="0.00"
                          min="0"
                          max={form.totalAmount}
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <CurrencyRupeeIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      </div>
                      {form.paymentStatus === 'TOTAL_CREDIT' && (
                        <div className="mt-2 text-center">
                          <span className="text-xs text-red-600 font-medium">
                            ❌ Auto-set to ₹0 (Total Credit)
                          </span>
                        </div>
                      )}
                      {form.paymentStatus === 'FULL_PAID' && (
                        <div className="mt-2 text-center">
                          <span className="text-xs text-green-600 font-medium">
                            ✅ Auto-set to {formatCurrency(form.totalAmount)} (Full Paid)
                          </span>
                        </div>
                      )}
                      {form.paymentStatus === 'ADVANCE_PARTIAL' && (
                        <div className="mt-2 text-center">
                          <span className="text-xs text-blue-600 font-medium">
                            💰 Enter partial payment amount
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Step 3: Enhanced Imported Items */}
                <div className={isDarkMode ? 'bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-blue-500 transition-colors' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-dashed border-blue-300 rounded-xl p-6 hover:border-blue-400 transition-colors'}>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                        3
                      </div>
                      <h3 className={`ml-3 text-lg font-semibold ${dc.text}`}>
                        Imported Items
                      </h3>
                      <span className="ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {form.items.length} items
                      </span>
                    </div>
                    {form.items.length > 0 && (
                      <div className={dc.textMutedSm}>
                        Total: <span className={`font-semibold ${dc.text}`}>{formatCurrency(form.subTotal)}</span>
                      </div>
                    )}
                  </div>
                  
                  {form.items.length > 0 ? (
                    <div className="space-y-4">
                      {form.items.map((item, index) => (
                        <div key={index} className={dc.itemCard}>
                          <div className="p-6">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start mb-3">
                                  <div className={`flex-shrink-0 w-6 h-6 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'} rounded-full flex items-center justify-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} font-medium text-xs mr-3 mt-1`}>
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className={`text-lg font-medium ${dc.text} break-words leading-relaxed`}>
                                      {item.itemName || item.Name || item.skuDescription}
                                    </h4>
                                    {item.itemDescription && (
                                      <p className="text-sm text-gray-600 mt-1 break-words leading-relaxed">
                                        {item.itemDescription}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
                                  <div className={dc.itemStatBox}>
                                    <div className={dc.itemStatLabel}>
                                      Brand
                                    </div>
                                    <div className={`text-sm font-medium ${dc.text}`}>
                                      {item.brandName || 'N/A'}
                                    </div>
                                  </div>
                                  
                                  <div className={dc.itemStatBox}>
                                    <div className={dc.itemStatLabel}>
                                      {item.pricingMode === 'sqft'
                                        ? `Coverage Area (${item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft'})`
                                        : ((item.calculationType === 'pcsPerBox' || item.item?.parameter?.calculationType === 'pcsPerBox' || (Number(item.boxes) > 0 || Number(item.pieces) > 0)) ? 'Boxes & Pieces' : 'Quantity')}
                                    </div>
                                    <div className={`text-sm font-medium ${dc.text}`}>
                                      {item.pricingMode === 'sqft' ? (
                                        <div>
                                          <div className="flex items-center">
                                            <span className="text-lg font-semibold text-purple-600">{item.coverageArea || 0}</span>
                                            <span className="ml-1 text-gray-500">{item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft'}</span>
                                          </div>
                                          {((item.boxes || 0) > 0 || (item.pieces || 0) > 0) && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              ≈ {item.boxes || 0} box, {item.pieces || 0} pcs (stock)
                                            </div>
                                          )}
                                          <div className="text-[10px] text-amber-600 mt-0.5">₹/{item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft'} pricing</div>
                                        </div>
                                      ) : (item.calculationType === 'pcsPerBox' || item.item?.parameter?.calculationType === 'pcsPerBox' || (Number(item.boxes) > 0 || Number(item.pieces) > 0)) ? (
                                        <div>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-lg font-semibold text-blue-600">{item.boxes || 0}</span>
                                            <span className="text-gray-500">boxes</span>
                                            <span className="text-gray-400">+</span>
                                            <span className="text-lg font-semibold text-blue-600">{item.pieces || 0}</span>
                                            <span className="text-gray-500">pcs</span>
                                          </div>
                                          {(item.coverageArea > 0) && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              Coverage: {item.coverageArea} {item.coverageAreaUnit || 'sqft'}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div>
                                          <div className="flex items-center">
                                            <span className="text-lg font-semibold text-blue-600">{item.quantity}</span>
                                            <span className="ml-1 text-gray-500">{item.unit || 'units'}</span>
                                          </div>
                                          {(item.coverageArea > 0) && (
                                            <div className="text-xs text-gray-500 mt-1">
                                              Coverage: {item.coverageArea} {item.coverageAreaUnit || 'sqft'}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  <div className={dc.itemStatBox}>
                                    <div className={dc.itemStatLabel}>
                                      Amount
                                    </div>
                                    <div className="text-sm font-semibold text-green-600 flex items-center">
                                      <CurrencyRupeeIcon className="h-4 w-4 mr-1" />
                                      {formatCurrency(item.amount || 0).replace('₹', '')}
                                    </div>
                                  </div>
                                  
                                  <div className={dc.itemStatBox}>
                                    <div className={dc.itemStatLabel}>
                                      Reserved
                                    </div>
                                    <div className={`text-sm font-medium ${dc.text} flex items-center justify-between`}>
                                      <div className="flex items-center">
                                        <div>
                                          <div className="flex items-center">
                                            <span className="text-lg font-semibold text-orange-600">{item.reservedQuantity || 0}</span>
                                            <span className="ml-1 text-gray-500">{(item.calculationType === 'pcsPerBox' || item.item?.parameter?.calculationType === 'pcsPerBox' || (Number(item.boxes) > 0 || Number(item.pieces) > 0)) ? 'pcs' : (item.unit || 'units')}</span>
                                          </div>
                                          {item.pricingMode === 'sqft' && (item.reservedCoverageArea || 0) > 0 && (
                                            <div className="text-xs text-amber-600 mt-0.5">
                                              {item.reservedCoverageArea} {item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft'} reserved
                                            </div>
                                          )}
                                        </div>
                                        {(item.reservedQuantity > 0) && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              fetchReservedDetails(item.item || item._id, null);
                                            }}
                                            className="text-purple-600 hover:text-purple-800 ml-2 p-1 rounded hover:bg-purple-50 transition-colors"
                                            title="View Reserved Details"
                                          >
                                            <InformationCircleIcon className="h-4 w-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div className={dc.itemStatBox}>
                                    <div className={`${dc.itemStatLabel} mb-2`}>
                                      Stock Status
                                    </div>
                                    <ReactSelect
                                      value={stockStatusOptions.find(opt => opt.value === item.stockStatus)}
                                      options={stockStatusOptions}
                                      isOptionDisabled={(option) => option.isDisabled}
                                      onChange={(selectedOption) => handleStockStatusChange(index, selectedOption.value)}
                                      className="react-select-container"
                                      classNamePrefix="react-select"
                                      styles={{
                                        control: (base, state) => ({
                                          ...base,
                                          minHeight: '36px',
                                          height: '36px',
                                          fontSize: '14px',
                                          backgroundColor: isDarkMode ? '#374151' : 'white',
                                          border: `2px solid ${isDarkMode ? '#4b5563' : '#e5e7eb'}`,
                                          borderColor: state.isFocused ? '#3b82f6' : (isDarkMode ? '#4b5563' : '#e5e7eb'),
                                          borderRadius: '8px',
                                          boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
                                          '&:hover': {
                                            borderColor: '#3b82f6'
                                          }
                                        }),
                                        singleValue: (base) => ({
                                          ...base,
                                          color: isDarkMode ? '#e5e7eb' : '#374151'
                                        }),
                                        valueContainer: (base) => ({
                                          ...base,
                                          height: '36px',
                                          padding: '0 12px'
                                        }),
                                        input: (base) => ({
                                          ...base,
                                          margin: '0px',
                                          color: isDarkMode ? '#e5e7eb' : '#374151'
                                        }),
                                        indicatorSeparator: () => ({
                                          display: 'none'
                                        }),
                                        indicatorsContainer: (base) => ({
                                          ...base,
                                          height: '36px'
                                        }),
                                        menu: (base) => ({
                                          ...base,
                                          zIndex: 9999,
                                          borderRadius: '8px',
                                          backgroundColor: isDarkMode ? '#374151' : 'white',
                                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                                        }),
                                        option: (base, state) => ({
                                          ...base,
                                          backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? (isDarkMode ? '#4b5563' : '#eff6ff') : (isDarkMode ? '#374151' : 'white'),
                                          color: state.isSelected ? 'white' : (isDarkMode ? '#e5e7eb' : '#374151'),
                                          '&:hover': {
                                            backgroundColor: state.isSelected ? '#3b82f6' : (isDarkMode ? '#4b5563' : '#eff6ff')
                                          }
                                        })
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className={`w-16 h-16 mx-auto mb-4 ${isDarkMode ? 'bg-gray-600' : 'bg-gray-100'} rounded-full flex items-center justify-center`}>
                        <DocumentDuplicateIcon className={`h-8 w-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
                      </div>
                      <h3 className={`text-lg font-medium ${dc.text} mb-2`}>No Items Yet</h3>
                      <p className={`${dc.textMuted} mb-4`}>
                        Select a quotation to import items automatically
                      </p>
                      <button
                        type="button"
                        onClick={openQuotationSelectionModal}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                      >
                        <PlusIcon className="h-4 w-4 mr-2" />
                        Select Quotation
                      </button>
                    </div>
                  )}
                </div>

                {/* Step 4: Enhanced Additional Charges */}
                <div className={isDarkMode ? 'bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-green-500 transition-colors' : 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-dashed border-green-300 rounded-xl p-6 hover:border-green-400 transition-colors'}>
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      4
                    </div>
                    <h3 className={`ml-3 text-lg font-semibold ${dc.text}`}>
                      Additional Charges
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className={dc.chargeInput}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Cartage
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.cartage}
                        onChange={(e) => {
                          const cartage = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, cartage }));
                          // Calculate totals with the new cartage value immediately
                          calculateTotals(form.items, {
                            cartage,
                            labour: form.labour || 0,
                            packingAndForwarding: form.packingAndForwarding || 0,
                            roundOff: form.roundOff || 0,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className={dc.chargeInput}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Packing & Forwarding
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.packingAndForwarding}
                        onChange={(e) => {
                          const packingAndForwarding = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, packingAndForwarding }));
                          // Calculate totals with the new packing value immediately
                          calculateTotals(form.items, {
                            cartage: form.cartage || 0,
                            labour: form.labour || 0,
                            packingAndForwarding,
                            roundOff: form.roundOff || 0,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className={dc.chargeInput}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Labour
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.labour}
                        onChange={(e) => {
                          const labour = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, labour }));
                          // Calculate totals with the new labour value immediately
                          calculateTotals(form.items, {
                            cartage: form.cartage || 0,
                            labour,
                            packingAndForwarding: form.packingAndForwarding || 0,
                            roundOff: form.roundOff || 0,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                    
                    <div className={dc.chargeInput}>
                      <label className={`block text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                        <div className="flex items-center">
                          <CurrencyRupeeIcon className="h-4 w-4 text-gray-400 mr-1" />
                          Round Off
                        </div>
                      </label>
                      <input
                        type="number"
                        value={form.roundOff}
                        onChange={(e) => {
                          const roundOff = parseFloat(e.target.value) || 0;
                          setForm(prev => ({ ...prev, roundOff }));
                          // Calculate totals with the new round off value immediately
                          calculateTotals(form.items, {
                            cartage: form.cartage || 0,
                            labour: form.labour || 0,
                            packingAndForwarding: form.packingAndForwarding || 0,
                            roundOff,
                            discountAmount: form.discountAmount || 0,
                            taxAmount: form.taxAmount || 0
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>

                {/* Step 5: Enhanced Total Summary */}
                <div className={isDarkMode ? 'bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-purple-500 transition-colors' : 'bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-dashed border-purple-300 rounded-xl p-6 hover:border-purple-400 transition-colors'}>
                  <div className="flex items-center mb-6">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      5
                    </div>
                    <h3 className={`ml-3 text-lg font-semibold ${dc.text}`}>
                      Total Summary
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className={dc.summaryCard}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${dc.textMuted}`}>Sub Total</p>
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-blue-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-blue-600">
                        {formatCurrency(form.subTotal)}
                      </p>
                    </div>
                    
                    <div className={dc.summaryCard}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${dc.textMuted}`}>Total Amount</p>
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-green-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-green-600">
                        {formatCurrency(form.totalAmount)}
                      </p>
                    </div>
                    
                    <div className={dc.summaryCard}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${dc.textMuted}`}>Advance Amount</p>
                        <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-yellow-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-yellow-600">
                        {formatCurrency(form.advanceAmount)}
                      </p>
                    </div>
                    
                    <div className={dc.summaryCard}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-medium ${dc.textMuted}`}>Balance Amount</p>
                        <div className="w-6 h-6 bg-red-100 rounded-full flex items-center justify-center">
                          <CurrencyRupeeIcon className="h-3 w-3 text-red-600" />
                        </div>
                      </div>
                      <p className="text-xl font-bold text-red-600">
                        {formatCurrency(form.balanceAmount)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Step 6: Enhanced Remarks */}
                <div className={isDarkMode ? 'bg-gray-700/50 border-2 border-dashed border-gray-600 rounded-xl p-6 hover:border-gray-500 transition-colors' : 'bg-gradient-to-r from-gray-50 to-slate-50 border-2 border-dashed border-gray-300 rounded-xl p-6 hover:border-gray-400 transition-colors'}>
                  <div className="flex items-center mb-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      6
                    </div>
                    <h3 className={`ml-3 text-lg font-semibold ${dc.text}`}>
                      Remarks
                    </h3>
                  </div>
                  
                  <div className={dc.chargeInput}>
                    <textarea
                      rows={4}
                      value={form.remarks}
                      onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-colors resize-none"
                      placeholder="Add any additional remarks or notes for this proforma invoice..."
                    />
                  </div>
                </div>

                {/* Enhanced Action Buttons */}
                <div className={`flex justify-end space-x-4 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <button
                    type="button"
                    onClick={() => {
                      setTabValue(0);
                      resetForm();
                    }}
                    className={`inline-flex items-center px-6 py-3 border ${isDarkMode ? 'border-gray-600 text-gray-300 bg-gray-700 hover:bg-gray-600' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'} rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors`}>
                    <XMarkIcon className="h-5 w-5 mr-2" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={!form.quotation || form.items.length === 0 || buttonLoading.submit}
                    className={`inline-flex items-center px-6 py-3 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 ${
                      buttonLoading.submit ? 'button-loading' : ''
                    }`}
                  >
                    {buttonLoading.submit ? (
                      <>
                        <div className="h-5 w-5 mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="h-5 w-5 mr-2" />
                        {edit ? 'Update' : 'Save'} Proforma Invoice
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </form>
        </div>
      )}

      <Modal
        show={quotationSelectionModal.open}
        onClose={() => {
          setQuotationSelectionModal({ open: false, step: 1 });
          setQuotationSearch('');
          setQuotationTypeFilter('all');
        }}
        title="Select Quotation"
        size="xl"
        isDarkMode={isDarkMode}
      >
        {quotationSelectionModal.open && (
          <>
            {/* Filter Bar */}
            <div className={`px-6 py-4 border-b ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'} flex-shrink-0`}>
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Search by ID, recipient, or amount..."
                    value={quotationSearch}
                    onChange={(e) => setQuotationSearch(e.target.value)}
                    className={`w-full px-4 py-2.5 pl-10 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>

                {/* Type Filter Tabs */}
                <div className={`flex rounded-lg p-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  {[
                    { value: 'all', label: 'All', count: quotations.length },
                    { value: 'quotation-new', label: 'Quotation', count: quotations.filter(q => q.type === 'quotation-new').length },
                    { value: 'tally-quotation', label: 'Tally', count: quotations.filter(q => q.type === 'tally-quotation').length }
                  ].map(tab => (
                    <button
                      key={tab.value}
                      onClick={() => setQuotationTypeFilter(tab.value)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                        quotationTypeFilter === tab.value
                          ? 'bg-blue-600 text-white shadow-sm'
                          : isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                      }`}>
                      {tab.label}
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                        quotationTypeFilter === tab.value
                          ? 'bg-blue-500 text-white'
                          : isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-300 text-gray-600'
                      }`}>{tab.count}</span>
                    </button>
                  ))}
                </div>

                {/* Sort */}
                <select
                  value={quotationSortBy}
                  onChange={(e) => setQuotationSortBy(e.target.value)}
                  className={`px-3 py-2 rounded-lg text-xs font-medium ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-700'} border focus:outline-none focus:ring-2 focus:ring-blue-500`}>
                  <option value="date-desc">Newest First</option>
                  <option value="date-asc">Oldest First</option>
                  <option value="amount-desc">Highest Amount</option>
                  <option value="amount-asc">Lowest Amount</option>
                  <option value="name-asc">Recipient A-Z</option>
                </select>
              </div>
            </div>

            {/* Quotation List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {(() => {
                const filtered = quotations
                  .filter(q => {
                    if (quotationTypeFilter !== 'all' && q.type !== quotationTypeFilter) return false;
                    if (!quotationSearch) return true;
                    const s = quotationSearch.toLowerCase();
                    return (
                      (q.quotationNumber || '').toLowerCase().includes(s) ||
                      (q.id || '').toLowerCase().includes(s) ||
                      (q.recipientName || '').toLowerCase().includes(s) ||
                      (q.totalAmount || '').toString().includes(s) ||
                      (q.typeLabel || '').toLowerCase().includes(s)
                    );
                  })
                  .sort((a, b) => {
                    switch (quotationSortBy) {
                      case 'date-asc': return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                      case 'amount-desc': return (b.totalAmount || 0) - (a.totalAmount || 0);
                      case 'amount-asc': return (a.totalAmount || 0) - (b.totalAmount || 0);
                      case 'name-asc': return (a.recipientName || '').localeCompare(b.recipientName || '');
                      default: return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                    }
                  });

                if (quotations.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className={`w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} flex items-center justify-center mb-4`}>
                        <svg className={`h-8 w-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h3 className={`text-lg font-medium ${dc.text} mb-1`}>No quotations available</h3>
                      <p className={dc.textMuted}>There are no approved quotations available for selection.</p>
                    </div>
                  );
                }

                if (filtered.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-16">
                      <div className={`w-16 h-16 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} flex items-center justify-center mb-4`}>
                        <svg className={`h-8 w-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                      <h3 className={`text-lg font-medium ${dc.text} mb-1`}>No matches found</h3>
                      <p className={dc.textMuted}>Try adjusting your search or filters.</p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {filtered.map((quotation) => (
                      <div
                        key={`${quotation._id}_${quotation.type}`}
                        onClick={() => handleQuotationSelect(quotation)}
                        className={dc.quotationCard}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`inline-flex px-2 py-0.5 text-[10px] font-bold uppercase rounded ${
                                quotation.type === 'quotation-new'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}>
                                {quotation.type === 'quotation-new' ? 'QTN' : 'TALLY'}
                              </span>
                              <span className={`text-sm font-semibold ${dc.text}`}>
                                {quotation.quotationNumber || quotation.id}
                              </span>
                              {quotation.status && (
                                <span className={`inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded ${
                                  quotation.status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                  quotation.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                                  isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}>{quotation.status}</span>
                              )}
                            </div>
                            <div className={`text-sm ${dc.text} truncate mb-1`}>
                              {quotation.recipientName || 'Unknown Recipient'}
                            </div>
                            <div className={dc.textMutedXs}>
                              {quotation.createdAt ? new Date(quotation.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}
                              {quotation.salesmen && quotation.salesmen.length > 0 && (
                                <span className="ml-2">| {quotation.salesmen.length} salesman(s)</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex flex-col items-end ml-4">
                            <p className="text-lg font-bold text-green-500">
                              {formatCurrency(quotation.totalAmount || 0)}
                            </p>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleQuotationSelect(quotation); }}
                              className="mt-2 px-4 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-sm group-hover:shadow-md">
                              Select
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className={`px-6 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} flex items-center justify-between flex-shrink-0`}>
              <span className={dc.textMutedSm}>
                {quotations.filter(q => quotationTypeFilter === 'all' || q.type === quotationTypeFilter).length} quotation(s) available
              </span>
              <button
                onClick={() => {
                  setQuotationSelectionModal({ open: false, step: 1 });
                  setQuotationSearch('');
                  setQuotationTypeFilter('all');
                }}
                className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Cancel
              </button>
            </div>
          </>
        )}
      </Modal>

      <Modal
        show={reservedDetailsModal.open}
        onClose={() => setReservedDetailsModal({ open: false, item: null, details: [] })}
        title={`Reserved Details - ${reservedDetailsModal.item?.itemName || 'Item'}`}
        size="lg"
        isDarkMode={isDarkMode}
      >
              {reservedDetailsModal.details.length > 0 ? (
                <>
                <div className={`grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  <div className={`rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-xs opacity-70">Total Reserved Quantity</div>
                    <div className="font-semibold text-orange-600">{reservedSummary.totalReservedQuantity.toFixed(2)}</div>
                  </div>
                  <div className={`rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-xs opacity-70">Total Reserved Area (sq.ft)</div>
                    <div className="font-semibold text-amber-600">{reservedSummary.totalReservedAreaSqft.toFixed(4)}</div>
                  </div>
                  <div className={`rounded-md px-3 py-2 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="text-xs opacity-70">Total Reserved Area (sq.m)</div>
                    <div className="font-semibold text-amber-600">{reservedSummary.totalReservedAreaSqm.toFixed(4)}</div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={dc.tableHead}>
                      <tr>
                        <th className={dc.tableTh}>Recipient</th>
                        <th className={dc.tableTh}>PI ID</th>
                        <th className={dc.tableTh}>Reserved Quantity</th>
                        <th className={dc.tableTh}>Reserved Area</th>
                        <th className={dc.tableTh}>Date Reserved</th>
                        <th className={dc.tableTh}>Status</th>
                      </tr>
                    </thead>
                    <tbody className={`${isDarkMode ? 'bg-gray-800 divide-gray-700' : 'bg-white divide-gray-200'} divide-y`}>
                      {reservedDetailsModal.details.map((detail, index) => (
                        <tr key={index} className={dc.tableRow}>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${dc.text}`}>
                            {detail.recipient?.name || detail.recipientName || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                            {detail.piId || detail.proformaInvoiceId || 'N/A'}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${dc.text}`}>
                            <span className="font-semibold text-orange-600">
                              {detail.reservedQuantity} {detail.unit || 'units'}
                            </span>
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${dc.text}`}>
                            {detail.pricingMode === 'sqft' ? (
                              <span className="font-semibold text-amber-600">
                                {detail.reservedCoverageArea || 0} {detail.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft'}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className={`px-6 py-4 whitespace-nowrap text-sm ${dc.text}`}>
                            {detail.dateReserved ? new Date(detail.dateReserved).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              detail.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                              detail.status === 'FULFILLED' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {detail.status || 'ACTIVE'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className={`${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mb-4`}>
                    <InformationCircleIcon className="mx-auto h-12 w-12" />
                  </div>
                  <p className={dc.textMuted}>No reserved details found for this item.</p>
                </div>
              )}
      </Modal>


      <Modal
        show={viewModal.open && !!viewModal.proformaInvoice}
        onClose={() => setViewModal({ open: false, proformaInvoice: null })}
        title={viewModal.proformaInvoice?.invoiceNumber || 'Proforma Invoice'}
        size="lg"
        isDarkMode={isDarkMode}
        headerActions={
          <>
            <CopyButton value={viewModal.proformaInvoice?.invoiceNumber} title="Copy invoice number" isDarkMode={isDarkMode} />
            <button
              onClick={() => handlePrint(viewModal.proformaInvoice)}
              className="inline-flex items-center px-3 py-1.5 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors"
            >
              Print
            </button>
          </>
        }
      >
        {viewModal.proformaInvoice && (
            <div className="space-y-3">
              <DetailSection title="Order" accent="blue" isDarkMode={isDarkMode}>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  <DetailField isDarkMode={isDarkMode} label="Status" value={<StatusBadge status={viewModal.proformaInvoice.status} />} />
                  <DetailField isDarkMode={isDarkMode} label="Delivery" value={<StatusBadge status={viewModal.proformaInvoice.deliveryStatus} />} />
                  <DetailField isDarkMode={isDarkMode} label="Payment" value={<StatusBadge status={viewModal.proformaInvoice.paymentStatus} />} />
                  <DetailField isDarkMode={isDarkMode} label="Recipient" value={viewModal.proformaInvoice.quotation?.recipient?.name || viewModal.proformaInvoice.quotation?.recipient?.businessName} />
                  <DetailField isDarkMode={isDarkMode} label="Salesperson" value={(viewModal.proformaInvoice.salesmen?.length ? viewModal.proformaInvoice.salesmen : viewModal.proformaInvoice.quotation?.salesmen)?.map(s => s.name).join(', ')} />
                  <DetailField isDarkMode={isDarkMode} label="Created By" value={viewModal.proformaInvoice.createdBy?.name} />
                  <DetailField isDarkMode={isDarkMode} label="Invoice Date" value={viewModal.proformaInvoice.invoiceDate ? new Date(viewModal.proformaInvoice.invoiceDate).toLocaleDateString('en-IN') : '-'} />
                  <DetailField isDarkMode={isDarkMode} label="Expected Delivery" value={viewModal.proformaInvoice.expectedDeliveryDate ? new Date(viewModal.proformaInvoice.expectedDeliveryDate).toLocaleDateString('en-IN') : '-'} />
                  <DetailField isDarkMode={isDarkMode} label="Days for Delivery" value={viewModal.proformaInvoice.daysRequiredForDelivery} />
                </dl>
              </DetailSection>

              <DetailSection title="Progress" accent="purple" isDarkMode={isDarkMode}>
                <div className="flex flex-wrap gap-x-6 gap-y-3">
                  {buildProgress(viewModal.proformaInvoice).map((step) => (
                    <div key={step.label} className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${step.done ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`} />
                      <div>
                        <p className={`text-sm font-medium ${step.done ? dc.text : dc.textMuted}`}>{step.label}</p>
                        <p className={`text-xs ${dc.textMuted}`}>
                          {step.date ? new Date(step.date).toLocaleDateString('en-IN') : step.done ? 'Done' : 'Pending'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </DetailSection>

              <DetailSection title="Financials" accent="green" isDarkMode={isDarkMode}>
                <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  <DetailField isDarkMode={isDarkMode} label="Sub Total" value={formatINR(viewModal.proformaInvoice.subTotal)} />
                  <DetailField isDarkMode={isDarkMode} label="Tax" value={formatINR(viewModal.proformaInvoice.taxAmount)} />
                  <DetailField isDarkMode={isDarkMode} label="Discount" value={formatINR(viewModal.proformaInvoice.discountAmount)} />
                  <DetailField isDarkMode={isDarkMode} label="Cartage" value={formatINR(viewModal.proformaInvoice.cartage)} />
                  <DetailField isDarkMode={isDarkMode} label="Packing & Forwarding" value={formatINR(viewModal.proformaInvoice.packingAndForwarding)} />
                  <DetailField isDarkMode={isDarkMode} label="Labour" value={formatINR(viewModal.proformaInvoice.labour)} />
                  <DetailField isDarkMode={isDarkMode} label="Round Off" value={formatINR(viewModal.proformaInvoice.roundOff)} />
                  <DetailField isDarkMode={isDarkMode} label="Total" value={<span className="font-semibold text-green-600">{formatINR(viewModal.proformaInvoice.totalAmount)}</span>} />
                  <DetailField isDarkMode={isDarkMode} label="Advance" value={formatINR(viewModal.proformaInvoice.advanceAmount)} />
                  <DetailField isDarkMode={isDarkMode} label="Balance" value={<span className="font-semibold text-red-600">{formatINR(viewModal.proformaInvoice.balanceAmount)}</span>} />
                </dl>
              </DetailSection>

              <DetailSection title={`Items (${viewModal.proformaInvoice.items?.length || 0})`} accent="purple" isDarkMode={isDarkMode}>
                {viewModal.proformaInvoice.items?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full">
                      <thead>
                        <tr className={`text-left text-xs uppercase tracking-wide ${dc.textMuted}`}>
                          <th className="px-3 py-2 font-medium">Item</th>
                          <th className="px-3 py-2 font-medium">Brand</th>
                          <th className="px-3 py-2 font-medium text-right">Qty</th>
                          <th className="px-3 py-2 font-medium text-right">Reserved</th>
                          <th className="px-3 py-2 font-medium text-right">Amount</th>
                          <th className="px-3 py-2 font-medium">Stock</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                        {viewModal.proformaInvoice.items.map((item, index) => {
                          const boxBased = item.calculationType === 'pcsPerBox' || item.item?.parameter?.calculationType === 'pcsPerBox' || Number(item.boxes) > 0 || Number(item.pieces) > 0;
                          return (
                            <tr key={index}>
                              <td className={`px-3 py-2 text-sm ${dc.text}`}>{item.itemName || item.Name || item.skuDescription}</td>
                              <td className={`px-3 py-2 text-sm ${dc.textMuted}`}>{item.brandName || item.item?.brand?.name || 'N/A'}</td>
                              <td className={`px-3 py-2 text-sm text-right ${dc.text}`}>
                                {boxBased ? `${item.boxes || 0} box, ${item.pieces || 0} pcs` : `${item.quantity || 0} ${item.unit || ''}`}
                                {item.coverageArea > 0 && <div className="text-xs text-gray-500">{item.coverageArea} {item.coverageAreaUnit || 'sqft'}</div>}
                              </td>
                              <td className="px-3 py-2 text-sm text-right text-orange-600">
                                {item.reservedQuantity || 0}{boxBased ? ' pcs' : ` ${item.unit || ''}`}
                                {item.pricingMode === 'sqft' && (item.reservedCoverageArea || 0) > 0 && (
                                  <div className="text-xs text-amber-600">{item.reservedCoverageArea} {item.coverageAreaUnit === 'sqm' ? 'sq.m' : 'sq.ft'}</div>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-right text-green-600">{formatINR(item.amount || 0)}</td>
                              <td className="px-3 py-2 text-sm"><StatusBadge status={item.stockStatus} /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className={dc.textMuted}>No items found</p>
                )}
              </DetailSection>

              {(viewLinks.indents.length > 0 || viewLinks.goodsReceipts.length > 0 || (viewModal.proformaInvoice.deliveryOrders || []).length > 0) && (
                <DetailSection title="Linked Documents" isDarkMode={isDarkMode}>
                  <div className="flex flex-wrap gap-2">
                    {viewLinks.indents.map(ind => (
                      <button
                        key={ind._id}
                        type="button"
                        onClick={() => navigate('/employee/purchase-indent')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-colors ${isDarkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        title="Open Purchase Indents"
                      >
                        <span className="opacity-70">Indent</span> {ind.indentNumber}
                        <StatusBadge status={ind.status} className="ml-1" />
                      </button>
                    ))}
                    {viewLinks.goodsReceipts.map(receipt => (
                      <button
                        key={receipt._id}
                        type="button"
                        onClick={() => navigate('/employee/goods-received')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-colors ${isDarkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        title="Open Goods Received"
                      >
                        <span className="opacity-70">GR</span> {receipt.receiptNumber}
                      </button>
                    ))}
                    {(viewModal.proformaInvoice.deliveryOrders || []).map(order => (
                      <button
                        key={order._id || order}
                        type="button"
                        onClick={() => navigate('/employee/delivery-order-new')}
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-colors ${isDarkMode ? 'border-gray-600 text-gray-200 hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                        title="Open Delivery Orders"
                      >
                        <span className="opacity-70">DO</span> {order.doNumber || 'View'}
                      </button>
                    ))}
                  </div>
                </DetailSection>
              )}

              {viewModal.proformaInvoice.remarks && (
                <DetailSection title="Remarks" isDarkMode={isDarkMode}>
                  <p className={`text-sm ${dc.text}`}>{viewModal.proformaInvoice.remarks}</p>
                </DetailSection>
              )}
            </div>
        )}
      </Modal>

      <Modal
        show={deliveryOrderModal.open}
        onClose={() => {
          setDeliveryOrderModal({ open: false, proformaInvoiceId: null, assignedOrders: [] });
          setSelectedDeliveryOrders([]);
        }}
        title="Delivery Orders Management"
        size="xl"
        isDarkMode={isDarkMode}
      >
        {deliveryOrderModal.open && (
          <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Already Assigned Orders */}
                <div className={`${
                  isDarkMode 
                    ? 'bg-green-950/20 border-green-900/60 shadow-inner' 
                    : 'bg-green-50 border-green-200'
                } border rounded-xl p-5`}>
                  <h4 className={`text-lg font-bold mb-4 flex items-center ${
                    isDarkMode ? 'text-green-400' : 'text-green-800'
                  }`}>
                    <CheckCircleIcon className="h-5 w-5 mr-2" />
                    Assigned Delivery Orders ({deliveryOrderModal.assignedOrders.length})
                  </h4>
                  
                  {deliveryOrderModal.assignedOrders.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {deliveryOrderModal.assignedOrders.map((order) => {
                        const targetPIId = deliveryOrderModal.proformaInvoiceId || completionModal.proformaInvoiceId;
                        const currentPI = proformaInvoices.find(p => p._id === targetPIId);
                        const piItems = currentPI ? currentPI.items : [];
                        return (
                          <div key={order._id} className={`${
                            isDarkMode 
                              ? 'bg-gray-800/80 border-green-900/40 text-gray-200 shadow-sm' 
                              : 'bg-white border-green-200 text-gray-900 shadow-xs'
                          } border rounded-lg p-4 transition-all duration-150`}>
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                  Order #{order.orderNumber || order._id}
                                </p>
                                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-650'}`}>
                                  🏢 {order.recipient?.name || order.recipient?.businessName || 'Unknown Recipient'}
                                </p>
                                <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                  📅 Assigned: {new Date(order.createdAt).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className={`text-sm font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                                  {formatCurrency(order.totalAmountWithGST || order.totalAmount || 0)}
                                </p>
                                <span className={`inline-flex items-center mt-2 px-2.5 py-0.5 text-xs font-semibold rounded-full border ${
                                  order.status === 'NEW' || order.status === 'NEW'
                                    ? (isDarkMode ? 'bg-blue-950/20 text-blue-300 border-blue-900/60' : 'bg-blue-50 text-blue-800 border-blue-200') : 
                                  order.status === 'COMPLETED' || order.status === 'Completed'
                                    ? (isDarkMode ? 'bg-green-950/20 text-green-300 border-green-900/60' : 'bg-green-55 text-green-800 border-green-200') :
                                    (isDarkMode ? 'bg-yellow-950/20 text-yellow-300 border-yellow-900/60' : 'bg-yellow-50 text-yellow-800 border-yellow-200')
                                }`}>
                                  <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                                    order.status === 'COMPLETED' || order.status === 'Completed' ? 'bg-green-500' : 'bg-blue-500'
                                  }`}></span>
                                  {order.status}
                                </span>
                              </div>
                            </div>

                            {/* Item-wise view with highlight mapping */}
                            {order.items && order.items.length > 0 && (
                              <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-700/60' : 'border-gray-100'} space-y-1.5`}>
                                <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-550'}`}>
                                  📦 Items in this Delivery Order:
                                </p>
                                <div className="grid grid-cols-1 gap-1.5">
                                  {order.items.map((orderItem, idx) => {
                                    const matchingPIItem = getMatchingPIItem(orderItem, piItems);
                                    return (
                                      <div 
                                        key={idx} 
                                        className={`flex justify-between items-center px-2.5 py-1.5 rounded-md text-xs font-medium border ${
                                          matchingPIItem 
                                            ? isDarkMode 
                                              ? 'bg-blue-950/40 border-blue-900/60 text-blue-200' 
                                              : 'bg-blue-50 border-blue-200 text-blue-800 shadow-xs'
                                            : isDarkMode
                                              ? 'bg-gray-800/40 text-gray-400 border-gray-700/50'
                                              : 'bg-gray-50 text-gray-550 border-gray-150'
                                        }`}
                                      >
                                        <div className="flex items-center space-x-1.5">
                                          <span className={`h-1.5 w-1.5 rounded-full ${matchingPIItem ? 'bg-blue-400 animate-pulse' : 'bg-gray-400'}`}></span>
                                          <span className="font-semibold">{orderItem.Name || orderItem.itemName || 'Unnamed Item'}</span>
                                          {matchingPIItem && (
                                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ml-1 rounded-full ${
                                              isDarkMode ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              Matches PI Items
                                            </span>
                                          )}
                                        </div>
                                        <div className="font-bold flex items-center space-x-2">
                                          <span>Qty: {orderItem.quantity} {orderItem.units || orderItem.unit || ''}</span>
                                          {matchingPIItem && (
                                            <span className={`text-[10px] ${isDarkMode ? 'text-blue-400' : 'text-blue-650'}`}>
                                              (PI: {matchingPIItem.quantity})
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50/5 rounded-lg border border-dashed border-gray-600/30">
                      <DocumentDuplicateIcon className="mx-auto h-10 w-10 text-green-500/50 mb-3" />
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-green-600'}`}>
                        No delivery orders assigned yet
                      </p>
                    </div>
                  )}
                </div>

                {/* Available Orders to Assign */}
                <div className={`${
                  isDarkMode 
                    ? 'bg-blue-950/20 border-blue-900/60 shadow-inner' 
                    : 'bg-blue-50 border-blue-200'
                } border rounded-xl p-5`}>
                  <h4 className={`text-lg font-bold mb-3 flex items-center ${
                    isDarkMode ? 'text-blue-400' : 'text-blue-800'
                  }`}>
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Available Delivery Orders ({availableDeliveryOrders.length})
                  </h4>
                  <p className={`text-sm mb-4 leading-relaxed ${isDarkMode ? 'text-blue-300/80' : 'text-blue-600'}`}>
                    Select which delivery orders belong to this proforma invoice. This updates the status of the proforma invoice to 'Partial' during transit/fulfillment.
                  </p>
                  
                  {availableDeliveryOrders.length > 0 ? (
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {availableDeliveryOrders.map((order) => {
                        const isSelected = selectedDeliveryOrders.includes(order._id);
                        const targetPIId = deliveryOrderModal.proformaInvoiceId || completionModal.proformaInvoiceId;
                        const currentPI = proformaInvoices.find(p => p._id === targetPIId);
                        const piItems = currentPI ? currentPI.items : [];
                        return (
                          <div key={order._id} className={`${
                            isDarkMode 
                              ? isSelected 
                                ? 'bg-blue-950/40 border-blue-500 shadow-md text-gray-200' 
                                : 'bg-gray-800/80 border-gray-700/60 text-gray-300 hover:bg-gray-850' 
                              : isSelected 
                                ? 'bg-blue-10/50 border-blue-300 shadow-xs' 
                                : 'bg-white border-blue-200 text-gray-900 hover:bg-blue-25'
                          } border rounded-lg p-4 transition-all duration-150`}>
                            <div className="flex items-start space-x-3">
                              <input
                                type="checkbox"
                                id={`order-${order._id}`}
                                checked={isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedDeliveryOrders([...selectedDeliveryOrders, order._id]);
                                  } else {
                                    setSelectedDeliveryOrders(selectedDeliveryOrders.filter(id => id !== order._id));
                                  }
                                }}
                                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1 cursor-pointer transition"
                              />
                              <label htmlFor={`order-${order._id}`} className="flex-1 cursor-pointer">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                      Order #{order.orderNumber || order._id}
                                    </p>
                                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-650'}`}>
                                      🏢 {order.recipient?.name || order.recipient?.businessName || 'Unknown Recipient'}
                                    </p>
                                    <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                      📅 Created: {new Date(order.createdAt).toLocaleDateString()}
                                    </p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <p className={`text-sm font-bold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                                      {formatCurrency(order.totalAmountWithGST || order.totalAmount || 0)}
                                    </p>
                                    <span className={`inline-flex mt-2 px-2 py-0.5 text-xs font-semibold rounded-full border ${
                                      order.status === 'NEW' || order.status === 'NEW'
                                        ? (isDarkMode ? 'bg-blue-950/20 text-blue-300 border-blue-900/60' : 'bg-blue-50 text-blue-800 border-blue-200') : 
                                      order.status === 'Pending' || order.status === 'PENDING'
                                        ? (isDarkMode ? 'bg-yellow-950/20 text-yellow-300 border-yellow-900/50' : 'bg-yellow-50 text-yellow-800 border-yellow-250') :
                                        (isDarkMode ? 'bg-gray-700/60 text-gray-300 border-gray-600/30' : 'bg-gray-100 text-gray-800 border-gray-200')
                                    }`}>
                                      {order.status}
                                    </span>
                                  </div>
                                </div>
                              </label>
                            </div>

                            {/* Item-wise view with highlight mapping */}
                            {order.items && order.items.length > 0 && (
                              <div className={`mt-3 pt-3 border-t ${isDarkMode ? 'border-gray-700/60' : 'border-gray-100'} space-y-1.5`}>
                                <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-550'}`}>
                                  📦 Items in this Delivery Order:
                                </p>
                                <div className="grid grid-cols-1 gap-1.5">
                                  {order.items.map((orderItem, idx) => {
                                    const matchingPIItem = getMatchingPIItem(orderItem, piItems);
                                    return (
                                      <div 
                                        key={idx} 
                                        className={`flex justify-between items-center px-2.5 py-1.5 rounded-md text-xs font-medium border ${
                                          matchingPIItem 
                                            ? isDarkMode 
                                              ? 'bg-blue-950/40 border-blue-900/60 text-blue-200' 
                                              : 'bg-blue-50 border-blue-200 text-blue-800 shadow-xs'
                                            : isDarkMode
                                              ? 'bg-gray-800/40 text-gray-400 border-gray-700/50'
                                              : 'bg-gray-50 text-gray-550 border-gray-150'
                                        }`}
                                      >
                                        <div className="flex items-center space-x-1.5">
                                          <span className={`h-1.5 w-1.5 rounded-full ${matchingPIItem ? 'bg-blue-400 animate-pulse' : 'bg-gray-400'}`}></span>
                                          <span className="font-semibold">{orderItem.Name || orderItem.itemName || 'Unnamed Item'}</span>
                                          {matchingPIItem && (
                                            <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ml-1 rounded-full ${
                                              isDarkMode ? 'bg-blue-900/60 text-blue-300' : 'bg-blue-100 text-blue-700'
                                            }`}>
                                              Matches PI Items
                                            </span>
                                          )}
                                        </div>
                                        <div className="font-bold flex items-center space-x-2">
                                          <span>Qty: {orderItem.quantity} {orderItem.units || orderItem.unit || ''}</span>
                                          {matchingPIItem && (
                                            <span className={`text-[10px] ${isDarkMode ? 'text-blue-400' : 'text-blue-650'}`}>
                                              (PI: {matchingPIItem.quantity})
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-10 bg-gray-50/5 rounded-lg border border-dashed border-gray-600/30">
                      <DocumentDuplicateIcon className="mx-auto h-10 w-10 text-blue-500/50 mb-3" />
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-blue-600'}`}>
                        No available delivery orders found
                      </p>
                    </div>
                  )}
                  
                  {availableDeliveryOrders.length > 0 && (
                    <div className={`mt-5 pt-4 border-t ${isDarkMode ? 'border-blue-900/50' : 'border-blue-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className={`text-sm ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                          Selected: <span className="font-bold underline">{selectedDeliveryOrders.length}</span> delivery order(s)
                        </p>
                        <button
                          onClick={() => setSelectedDeliveryOrders([])}
                          className={`text-xs font-semibold ${isDarkMode ? 'text-blue-400 hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'}`}
                        >
                          Clear Selection
                        </button>
                      </div>
                      <button
                        onClick={handleAssignDeliveryOrders}
                        disabled={selectedDeliveryOrders.length === 0 || buttonLoading['assign-delivery-orders']}
                        className={`w-full bg-blue-600 text-white font-semibold px-4 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${
                          buttonLoading['assign-delivery-orders'] ? 'button-loading' : ''
                        }`}
                      >
                        {buttonLoading['assign-delivery-orders'] ? 'Assigning...' : `Assign ${selectedDeliveryOrders.length} Order(s) to Invoice`}
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    setDeliveryOrderModal({ open: false, proformaInvoiceId: null, assignedOrders: [] });
                    setSelectedDeliveryOrders([]);
                  }}
                  className={`px-6 py-2.5 rounded-lg font-semibold transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Close
                </button>
              </div>
          </>
        )}
      </Modal>

      <Modal
        show={completionModal.open}
        onClose={() => setCompletionModal({ open: false, proformaInvoiceId: null, deliveryOrders: [], selectedLastDeliveryOrderIds: [] })}
        title="Complete Proforma Invoice"
        size="md"
        isDarkMode={isDarkMode}
      >
        {completionModal.open && (
          <>
            <p className={`${dc.textMuted} mb-4 leading-relaxed`}>
                Please select the delivery orders confirming all items are issued to mark this Proforma Invoice as completed:
              </p>
              
              <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                {completionModal.deliveryOrders.map((order) => {
                  const isSelected = completionModal.selectedLastDeliveryOrderIds.includes(order._id);
                  return (
                    <div key={order._id} className={`border rounded-lg p-4 transition-all duration-150 ${
                      isSelected 
                        ? (isDarkMode ? 'border-blue-500 bg-blue-950/25 shadow-md shadow-blue-500/5' : 'border-blue-400 bg-blue-50')
                        : (isDarkMode ? 'border-gray-700/70 hover:bg-gray-700/30 text-gray-300' : 'border-gray-200 hover:bg-gray-50')
                    }`}>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id={`delivery-order-${order._id}`}
                          name="lastDeliveryOrder"
                          value={order._id}
                          checked={isSelected}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setCompletionModal(prev => ({
                              ...prev,
                              selectedLastDeliveryOrderIds: isChecked
                                ? [...prev.selectedLastDeliveryOrderIds, order._id]
                                : prev.selectedLastDeliveryOrderIds.filter(id => id !== order._id)
                            }));
                          }}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                        />
                        <label htmlFor={`delivery-order-${order._id}`} className="ml-3 cursor-pointer flex-1">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className={`text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-950'}`}>
                                📦 DO Number: <span className="font-bold underline">{order.doNumber || order.Manual_dO_Number || 'N/A'}</span>
                              </p>
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-700'}`}>
                                🏢 Recipient: {order.recipient?.name || order.recipient?.businessName || 'N/A'}
                              </p>
                              <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                📅 Created: {new Date(order.createdAt).toLocaleDateString()}
                              </p>
                            </div>
                            <span className={`inline-flex px-2.5 py-0.5 text-xs font-semibold rounded-full border ${
                              order.status === 'Completed' || order.status === 'COMPLETED'
                                ? (isDarkMode ? 'bg-green-950/30 text-green-300 border-green-800/60' : 'bg-green-50 text-green-800 border-green-200') 
                                : order.status === 'Pending' || order.status === 'PENDING'
                                ? (isDarkMode ? 'bg-yellow-950/20 text-yellow-300 border-yellow-800/40' : 'bg-yellow-50 text-yellow-800 border-yellow-200') 
                                : (isDarkMode ? 'bg-gray-700/60 text-gray-300' : 'bg-gray-100 text-gray-800')
                            }`}>
                              {order.status}
                            </span>
                          </div>
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setCompletionModal({
                    open: false,
                    proformaInvoiceId: null,
                    deliveryOrders: [],
                    selectedLastDeliveryOrderIds: []
                  })}
                  className={`px-5 py-2.5 rounded-lg font-semibold transition-colors ${
                    isDarkMode 
                      ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (completionModal.selectedLastDeliveryOrderIds.length > 0) {
                      completeProformaInvoice(completionModal.proformaInvoiceId, completionModal.selectedLastDeliveryOrderIds);
                    } else {
                      showErrorAlert('Error', 'Please select at least one delivery order.');
                    }
                  }}
                  disabled={completionModal.selectedLastDeliveryOrderIds.length === 0}
                  className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-40 transition-all shadow-sm"
                >Complete Invoice</button>
              </div>
          </>
        )}
      </Modal>

      <Modal
        show={keyboardHelp}
        onClose={() => setKeyboardHelp(false)}
        title="Keyboard Shortcuts"
        size="md"
        isDarkMode={isDarkMode}
      >
        <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className={`font-semibold ${dc.text} mb-3`}>General</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>New Invoice</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>Ctrl+N</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Save Form</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>Ctrl+S</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Cancel/Close</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>Ctrl+W</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Search</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>Ctrl+/</kbd>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className={`font-semibold ${dc.text} mb-3`}>Navigation</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Previous Page</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>←</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Next Page</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>→</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Close Modal</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>Esc</kbd>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={dc.textMuted}>Show/Hide Help</span>
                      <kbd className={`px-2 py-1 ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'} rounded text-sm`}>Ctrl+?</kbd>
                    </div>
                  </div>
                </div>
              </div>
              <div className={`mt-6 p-4 ${isDarkMode ? 'bg-blue-900/30' : 'bg-blue-50'} rounded-lg`}>
                <h4 className={`font-semibold ${isDarkMode ? 'text-blue-300' : 'text-blue-900'} mb-2`}>Tips</h4>
                <ul className={`${isDarkMode ? 'text-blue-300' : 'text-blue-800'} text-sm space-y-1`}>
                  <li>• Use Tab to navigate between form fields</li>
                  <li>• Most actions work with Ctrl/Cmd key combinations</li>
                  <li>• Press Esc to close any open modal or dialog</li>
                  <li>• Arrow keys work for pagination when not in a form</li>
                </ul>
              </div>
            </div>
            <div className={`px-6 py-4 ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-b-xl`}>
              <div className="flex justify-end">
                <button
                  onClick={() => setKeyboardHelp(false)}
                  className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-600 text-gray-200 hover:bg-gray-500' : 'bg-gray-600 text-white hover:bg-gray-700'}`}
                >
                  Got it!
                </button>
              </div>
        </div>
      </Modal>

      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 max-w-md w-full transform transition-all duration-500 ${
          notification.show ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}>
          <div className={`p-4 rounded-lg shadow-lg border-l-4 ${
            notification.type === 'success' ? 'bg-green-50 border-green-400' :
            notification.type === 'error' ? 'bg-red-50 border-red-400' :
            notification.type === 'warning' ? 'bg-yellow-50 border-yellow-400' :
            'bg-blue-50 border-blue-400'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {notification.type === 'success' && (
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                )}
                {notification.type === 'error' && (
                  <XCircleIcon className="h-5 w-5 text-red-400" />
                )}
                {notification.type === 'warning' && (
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                )}
                {notification.type === 'info' && (
                  <InformationCircleIcon className="h-5 w-5 text-blue-400" />
                )}
              </div>
              <div className="ml-3">
                <p className={`text-sm font-medium ${
                  notification.type === 'success' ? 'text-green-800' :
                  notification.type === 'error' ? 'text-red-800' :
                  notification.type === 'warning' ? 'text-yellow-800' :
                  'text-blue-800'
                }`}>
                  {notification.message}
                </p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                    className={`inline-flex rounded-md p-1.5 transition-colors ${
                      notification.type === 'success' ? 'text-green-500 hover:bg-green-100' :
                      notification.type === 'error' ? 'text-red-500 hover:bg-red-100' :
                      notification.type === 'warning' ? 'text-yellow-500 hover:bg-yellow-100' :
                      'text-blue-500 hover:bg-blue-100'
                    }`}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Global Loading Overlay */}
      {isAnimating && (
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40 flex items-center justify-center">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-xl animate-pulse`}>
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className={`text-lg font-medium ${dc.text}`}>Processing...</span>
            </div>
          </div>
        </div>
      )}

      <Modal
        show={stockStatusModal.open}
        onClose={() => setStockStatusModal({ open: false, proformaInvoice: null, selectedItems: [] })}
        title="Update Stock Status"
        size="lg"
        isDarkMode={isDarkMode}
      >
        {stockStatusModal.open && (
          <>
              <div className="mb-6">
                <p className={`text-sm ${dc.textMuted} mb-4`}>
                  Select items to update from <span className="font-semibold text-orange-600">"Reserved / Goods Received"</span> to <span className="font-semibold text-green-600">"To Be Billed"</span> status:
                </p>
                
                <div className={`${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg p-4`}>
                  <div className="space-y-3">
                    {stockStatusModal.proformaInvoice?.items
                      ?.filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus))
                      ?.map((item, index) => (
                        <div key={item._id || index} className={`flex items-center space-x-3 p-3 ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'} rounded border`}>
                          <input
                            type="checkbox"
                            id={`item-${item._id || index}`}
                            checked={stockStatusModal.selectedItems.includes(item._id)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              const itemId = item._id;
                              
                              setStockStatusModal(prev => ({
                                ...prev,
                                selectedItems: isChecked 
                                  ? [...prev.selectedItems, itemId]
                                  : prev.selectedItems.filter(id => id !== itemId)
                              }));
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <label 
                            htmlFor={`item-${item._id || index}`}
                            className="flex-1 cursor-pointer"
                          >
                            <div className="flex justify-between items-center">
                              <div>
                                <span className={`font-medium ${dc.text}`}>
                                  {item.itemName || item.description || 'Unnamed Item'}
                                </span>
                                {item.specification && (
                                  <span className="text-sm text-gray-500 ml-2">
                                    - {item.specification}
                                  </span>
                                )}
                                <div className={`text-sm ${dc.textMuted}`}>
                                  Qty: {item.quantity} {item.unit || ''}
                                  {item.rate && (
                                    <span className="ml-2">
                                      @ ₹{item.rate}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="text-right">
                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded-full">
                                  {item.stockStatus === 'goods-received' ? 'Goods Received' : 'To be Reserved'}
                                </span>
                                {item.amount && (
                                  <div className={`text-sm font-medium ${dc.text} mt-1`}>
                                    ₹{item.amount.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </label>
                        </div>
                      ))
                    }
                    
                    {stockStatusModal.proformaInvoice?.items?.filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus)).length === 0 && (
                      <div className="text-center py-8">
                        <InformationCircleIcon className={`mx-auto h-12 w-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <h3 className={`mt-2 text-sm font-medium ${dc.text}`}>No items to update</h3>
                        <p className={`mt-1 text-sm ${dc.textMuted}`}>All items are already in "To Be Billed" status or other statuses.</p>
                      </div>
                    )}
                  </div>
                  
                  {stockStatusModal.proformaInvoice?.items?.filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus)).length > 0 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            const eligibleItems = stockStatusModal.proformaInvoice.items
                              .filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus))
                              .map(item => item._id);
                            
                            setStockStatusModal(prev => ({
                              ...prev,
                              selectedItems: [...eligibleItems]
                            }));
                          }}
                          className="text-sm text-blue-600 hover:text-blue-500"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setStockStatusModal(prev => ({
                              ...prev,
                              selectedItems: []
                            }));
                          }}
                          className="text-sm text-gray-600 hover:text-gray-500"
                        >
                          Select None
                        </button>
                      </div>
                      <span className={`text-sm ${dc.textMuted}`}>
                        {stockStatusModal.selectedItems.length} of {stockStatusModal.proformaInvoice?.items?.filter(item => ['to-be-reserved', 'goods-received'].includes(item.stockStatus)).length} items selected
                      </span>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setStockStatusModal({ open: false, proformaInvoice: null, selectedItems: [] })}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${isDarkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'} border focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmStockStatusUpdate}
                  disabled={stockStatusModal.selectedItems.length === 0 || buttonLoading[`update-stock-${stockStatusModal.proformaInvoice?._id}`]}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${
                    stockStatusModal.selectedItems.length === 0 || buttonLoading[`update-stock-${stockStatusModal.proformaInvoice?._id}`]
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {buttonLoading[`update-stock-${stockStatusModal.proformaInvoice?._id}`] ? (
                    <div className="flex items-center">
                      <div className="animate-spin h-4 w-4 border border-white border-t-transparent rounded-full mr-2"></div>
                      Updating...
                    </div>
                  ) : (
                    `Update ${stockStatusModal.selectedItems.length} Item${stockStatusModal.selectedItems.length !== 1 ? 's' : ''}`
                  )}
                </button>
              </div>
          </>
        )}
      </Modal>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            transform: scale(1);
            opacity: 1;
          }
          to {
            transform: scale(0.8);
            opacity: 0;
          }
        }
        
        @keyframes bounce {
          0%, 20%, 53%, 80%, 100% {
            transform: translate3d(0, 0, 0);
          }
          40%, 43% {
            transform: translate3d(0, -10px, 0);
          }
          70% {
            transform: translate3d(0, -5px, 0);
          }
          90% {
            transform: translate3d(0, -2px, 0);
          }
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
          100% {
            transform: scale(1);
          }
        }
        
        @keyframes highlight {
          0% {
            background-color: transparent;
          }
          50% {
            background-color: rgba(59, 130, 246, 0.1);
          }
          100% {
            background-color: transparent;
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.5s ease-out;
        }
        
        .animate-fadeOut {
          animation: fadeOut 0.5s ease-out;
        }
        
        .animate-bounce {
          animation: bounce 1s ease-in-out;
        }
        
        .animate-pulse-custom {
          animation: pulse 0.5s ease-in-out;
        }
        
        .animate-highlight {
          animation: highlight 1s ease-in-out;
        }
        
        .button-loading {
          position: relative;
          pointer-events: none;
          opacity: 0.7;
        }
        
        .button-loading::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          width: 20px;
          height: 20px;
          margin-left: -10px;
          margin-top: -10px;
          border: 2px solid transparent;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        
        .confetti {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: 9999;
        }
        
        .confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          background: linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #f9ca24);
          animation: confetti-fall 3s linear infinite;
        }
        
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
      `}</style>
      </div>
    </div>
  );
};

export default ProformaInvoice;